import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { PDI } from "../data/pdi"
import { Field, Input, Label, Textarea } from "../../../components/forms";
import { ButtonLight, ButtonSecondary } from "../../../components/ctas";
import { CheckboxItem } from "../qc/page";
import { useAppContext } from "../../../contexts/app";
import { useCategoryNav } from "../../../components/globals";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { filterCategories } from "../utils/accessControl";
import { UseGoogleSheet } from "../../../hooks/UseGoogleSheets";
import { use } from "react";
import { comparePDIToKeys } from "../inventory2/page";
import { useGoogleStorageImage } from "../../../hooks/useGoogleStorageImage";


const removeSpecialCharacters = (str) => {
	return str.replace(/[^\w\s]/gi, '').replace(/\s+/g, '_').toLowerCase();
}


export const CheckBox = ({ slug, name, required, formValues, setFormValues, initialLoadValues, datalist }) => {
	const { currentUser } = useAuth();
	const [activeIndex, setActiveIndex] = useState(null);
	const [addDetails, setAddDetails] = useState(false);
	const [updatedBy, setUpdatedBy] = useState(null);
	const [updatedOn, setUpdatedOn] = useState(null);
	const [detailsBy, setDetailsBy] = useState(null);
	const [detailsOn, setDetailsOn] = useState(null);
	const [initialUpdatedOn, setInitialUpdatedOn] = useState(null);
	const [initialDetailsOn, setInitialDetailsOn] = useState(null);

	const handleAddDetails = (e) => {
		if (e) e.preventDefault();
		const newValue = !addDetails;
		setAddDetails(newValue);

		// Only delete fields when user explicitly hides details
		if (newValue === false) {
			setFormValues(prev => {
				const updated = { ...prev };
				delete updated[`${slug}_Details`];
				delete updated[`${slug}_Details_Timestamp`];
				delete updated[`${slug}_Details_User`];
				return updated;
			});
		}
	}

	useEffect(() => {
		if (activeIndex) {
			const val = activeIndex === 1 ? "Yes" : activeIndex === 2 ? "N/A" : "No";
			handleField({ target: { id: slug, value: val } });
		}
	}, [activeIndex]);


	const handleCheckbox = (e, value) => {
		if (e) e.preventDefault();
		setActiveIndex(value);
	}

	const handleField = (e) => {
		if (setFormValues && activeIndex) {
			// Only update timestamp if the value is actually changing
			const currentValue = formValues[e.target.id];
			const isValueChanging = currentValue !== e.target.value;

			if (isValueChanging) {
				setFormValues(prev => ({
					...prev,
					[e.target.id]: e.target.value,
					[`${e.target.id}_Timestamp`]: new Date().toISOString(),
					[`${e.target.id}_User`]: currentUser.userinfo ? currentUser.userinfo?.name : 'Unknown'
				}));
			} else {
				// Value hasn't changed, just set the value without updating timestamp
				setFormValues(prev => ({
					...prev,
					[e.target.id]: e.target.value
				}));
			}
		}
	}

	const indexMapReverse = value => {
		if (value === "Yes") {
			return 1;
		} else if (value === "N/A") {
			return 2;
		} else if (value === "No") {
			return 3;
		} else {
			return null;
		}
	}

	let count = 0;

	useLayoutEffect(() => {
		if (slug && formValues[slug]) {
			// Only set activeIndex if there's a corresponding timestamp
			// This prevents showing checkboxes as checked when they haven't been completed
			if (formValues[`${slug}_Timestamp`]) {
				if (formValues[`${slug}_Details`]) setAddDetails(true);
				setActiveIndex(indexMapReverse(formValues[slug]));
			} else {
				// If no timestamp, clear the activeIndex to show unchecked state
				setActiveIndex(null);
				setAddDetails(false);
			}
		} else {
			setActiveIndex(null);
			setAddDetails(false);
		}
	}, [slug, formValues, initialLoadValues]);



	useEffect(() => {
		if (formValues) {
			setUpdatedBy(formValues[`${slug}_User`] || null);
			setDetailsBy(formValues[`${slug}_Details_User`] || null);

			setDetailsOn(formValues[`${slug}_Details_Timestamp`] && new Date(formValues[`${slug}_Details_Timestamp`]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || null);
			setUpdatedOn(formValues[`${slug}_Timestamp`] && new Date(formValues[`${slug}_Timestamp`]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || null);
		}
	}, [formValues]);

	return <>
		<div>
			<div id={ slug || null } className="tw-grid tw-grid-cols-3 tw-py-5px tw-w-full">
				<CheckboxItem onClick={e => handleCheckbox(e, 1)} datalist={datalist || null} initialLoadValues={initialLoadValues} activeIndex={activeIndex} setActiveIndex={setActiveIndex} name={`${name}1`} id={slug} index={1} required={required} value="Yes" />
				<CheckboxItem onClick={e => handleCheckbox(e, 2)} datalist={datalist || null} initialLoadValues={initialLoadValues} activeIndex={activeIndex} setActiveIndex={setActiveIndex} name={`${name}2`} id={slug} index={2} required={required} value="N/A" />
				<CheckboxItem onClick={e => handleCheckbox(e, 3)} datalist={datalist || null} initialLoadValues={initialLoadValues} activeIndex={activeIndex} setActiveIndex={setActiveIndex} name={`${name}3`} id={slug} index={3} required={required} value="No" />
			</div>
			{formValues[`${slug}_User`] ? <span className={`tw-mt-2px tw-opacity-80 tw-block tw-text-[.5em] tw-uppercase tw-tracking-wider tw-leading-none tw-mb-10px`}>Updated by: {formValues[`${slug}_User`] || 'N/A'} on {formValues[`${slug}_Timestamp`] && new Date(formValues[`${slug}_Timestamp`]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || 'N/A'}</span> : <></>}

			<div className={`tw-transition-all tw-duration-300 tw-delay-50 tw-overflow-clip ${activeIndex === 1 || activeIndex === 2 || activeIndex === 3 ? 'tw-max-h-40px' : 'tw-max-h-0px'} `}>
				<ButtonLight onClick={e => handleAddDetails(e)} className="tw-min-h-25px tw-items-center">
					<span className="tw-text-[.7em] tw-uppercase tw-font-quaternary tw-font-quaternary-bold tw-tracking-wide tw-px-10px tw-items-center tw-flex tw-w-full">
						{addDetails ? 'Hide ' : 'Add '} Details
					</span>
				</ButtonLight>
			</div>
			{addDetails == true && <div>
				<Textarea value={formValues[`${slug}_Details`] || ""} onInput={handleField} id={slug + "_Details"} required={activeIndex === 3 ? "required" : null} placeholder={activeIndex === 3 ? `Provide Details for Why It Did Not Pass` : `Provide Details`} className="tw-w-full" />
				{formValues[`${slug}_Details_User`] ? <>
					<span className={`tw-block tw-text-[.5em] tw-uppercase tw-tracking-wider tw-leading-none ${initialUpdatedOn !== updatedOn ? ' !XXXtw-text-red-600' : ''}`}> Details Updated by: {detailsBy || 'N/A'} on {detailsOn || 'N/A'}</span>
				</> : <></>}
			</div>}
		</div>
	</>

}

const UnitModelSelector = ({ field, formValues, setFormValues }) => {
	const { currentUser } = useAuth();
	const [activeIndex, setActiveIndex] = useState(1); // Default to "Flock Trailer" (index 1)
	const [initialized, setInitialized] = useState(false);
	const options = ["Flock Trailer", "Flock Gen Trailer"];
	const defaultOption = "Flock Trailer";

	// Generator-related items that should be set to N/A when Flock Trailer is selected
	const cqcGenItems = [
		// QC checkbox items with cqc: 'cqc-gen'
		"gen_securement",
		"lp_monitors_qc",
		"lp_tanks_qc",
		"ags_installation_qc",
		"check_fuel_lines_qc",
		"ags_functionality_qc",
		"gen_run_test_outdoor",
		// Generator details form fields
		"gen_gen_sn",
		"gen_gen_model",
		"gen_details_lp_monitor_1",
		"gen_details_lp_monitor_2",
		"gen_details_ags_model",
		"gen_details_ags_firmware"
	];

	// Helper to get last 6 digits of VIN
	const getUnitIdentifierName = (model, vin) => {
		const vinValue = vin || "";
		const last6 = vinValue.length >= 6 ? vinValue.slice(-6) : vinValue;
		return `${model} ${last6}`.trim();
	};

	// Helper to get cqc-gen item updates based on model selection
	const getCqcGenUpdates = (model) => {
		const updates = {};
		const timestamp = new Date().toISOString();
		const user = currentUser?.userinfo?.name || 'Unknown';

		if (model === "Flock Trailer") {
			// Set all cqc-gen items to N/A when Flock Trailer is selected
			cqcGenItems.forEach(slug => {
				updates[slug] = "N/A";
				updates[`${slug}_Timestamp`] = timestamp;
				updates[`${slug}_User`] = user;
			});
		} else {
			// Clear the N/A values when Flock Gen Trailer is selected (if they were set to N/A)
			cqcGenItems.forEach(slug => {
				if (formValues[slug] === "N/A") {
					updates[slug] = "";
					updates[`${slug}_Timestamp`] = "";
					updates[`${slug}_User`] = "";
				}
			});
		}
		return updates;
	};

	// Sync activeIndex with formValues when value changes (handles async data loading)
	useLayoutEffect(() => {
		const savedValue = formValues[field.id];
		if (savedValue) {
			const index = options.indexOf(savedValue);
			if (index !== -1) {
				setActiveIndex(index + 1);
			}
		}
	}, [formValues[field.id]]);

	// Initialize defaults on mount (only for new entries)
	useLayoutEffect(() => {
		if (initialized) return;

		if (!formValues[field.id]) {
			// Set default value if no saved value exists
			setActiveIndex(1); // "Flock Trailer" is at index 1
			const unitIdentifierName = getUnitIdentifierName(defaultOption, formValues?.VIN);
			const cqcGenUpdates = getCqcGenUpdates(defaultOption);
			setFormValues(prev => ({
				...prev,
				[field.id]: defaultOption,
				[`${field.id}_Timestamp`]: new Date().toISOString(),
				[`${field.id}_User`]: currentUser?.userinfo?.name || 'Unknown',
				"unit_identifiers_unit_identifier_name": unitIdentifierName,
				"unit_identifiers_unit_identifier_name_Timestamp": new Date().toISOString(),
				"unit_identifiers_unit_identifier_name_User": currentUser?.userinfo?.name || 'Unknown',
				...cqcGenUpdates
			}));
		}
		setInitialized(true);
	}, []);

	// Update unit identifier name when VIN changes (after initialization)
	useEffect(() => {
		if (!initialized) return;
		const currentModel = formValues[field.id] || defaultOption;
		const newUnitIdentifierName = getUnitIdentifierName(currentModel, formValues?.VIN);
		const currentUnitIdentifierName = formValues["unit_identifiers_unit_identifier_name"];

		// Only update if the computed name is different
		if (newUnitIdentifierName !== currentUnitIdentifierName) {
			setFormValues(prev => ({
				...prev,
				"unit_identifiers_unit_identifier_name": newUnitIdentifierName,
				"unit_identifiers_unit_identifier_name_Timestamp": new Date().toISOString(),
				"unit_identifiers_unit_identifier_name_User": currentUser?.userinfo?.name || 'Unknown'
			}));
		}
	}, [formValues?.VIN, initialized]);

	const handleSelect = (e, index) => {
		if (e) e.preventDefault();
		setActiveIndex(index);
		const selectedValue = options[index - 1]; // Convert back to 0-indexed
		const unitIdentifierName = getUnitIdentifierName(selectedValue, formValues?.VIN);
		const cqcGenUpdates = getCqcGenUpdates(selectedValue);

		setFormValues(prev => ({
			...prev,
			[field.id]: selectedValue,
			[`${field.id}_Timestamp`]: new Date().toISOString(),
			[`${field.id}_User`]: currentUser?.userinfo?.name || 'Unknown',
			"unit_identifiers_unit_identifier_name": unitIdentifierName,
			"unit_identifiers_unit_identifier_name_Timestamp": new Date().toISOString(),
			"unit_identifiers_unit_identifier_name_User": currentUser?.userinfo?.name || 'Unknown',
			...cqcGenUpdates
		}));
	};

	return (
		<div className="tw-flex tw-flex-col tw-gap-5px">
			<label className="tw-text-sm tw-font-quaternary tw-font-quaternary-bold tw-uppercase tw-tracking-wider">
				<span dangerouslySetInnerHTML={{ __html: field.label }} />
			</label>
			{field.description && (
				<span className="tw-text-xs tw-opacity-70 tw-mb-5px">{field.description}</span>
			)}
			<div id={field.id} className="tw-grid tw-grid-cols-2 tw-py-5px tw-w-full -tw-gap-1px">
				{options.map((option, idx) => (
					<CheckboxItem
						key={idx}
						onClick={e => handleSelect(e, idx + 1)}
						activeIndex={activeIndex}
						setActiveIndex={setActiveIndex}
						name={`${field.id}_${idx + 1}`}
						id={field.id}
						index={idx + 1}
						value={option}
					/>
				))}
			</div>
			{formValues[`${field.id}_User`] && (
				<span className="tw-mt-2px tw-opacity-80 tw-block tw-text-[.5em] tw-uppercase tw-tracking-wider tw-leading-none">
					Updated by: {formValues[`${field.id}_User`] || 'N/A'} on {formValues[`${field.id}_Timestamp`] && new Date(formValues[`${field.id}_Timestamp`]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || 'N/A'}
				</span>
			)}
		</div>
	);
};

const GeneratorSerialSelector = ({ field, formValues, setFormValues, isEditMode }) => {
	const { currentUser } = useAuth();
	const modelOptions = ["2800i", "2800e"];
	const prefixOptions = [
		"4L1R0AA3BS",
		"4L1R0AA3S",
		"4L1R0AB2P",
		"4L1R0AB2R",
		"4L1R0AB2S",
		"4L1R0AB3S",
		"4L1R0AB4S",
		"4L1R0AB4T"
	];
	const defaultModel = "2800i";

	const [selectedModel, setSelectedModel] = useState(null);
	const [selectedPrefix, setSelectedPrefix] = useState(null);
	const [lastSixDigits, setLastSixDigits] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const [directSerialInput, setDirectSerialInput] = useState("");

	// Initialize on mount - check if editing existing serial
	useLayoutEffect(() => {
		const existingSerial = formValues["gen_gen_sn"];
		const existingModel = formValues["gen_gen_model"];

		// Treat "N/A" or empty values as non-existing
		const hasValidSerial = existingSerial && existingSerial.length > 0 && existingSerial !== "N/A";
		const hasValidModel = existingModel && existingModel !== "N/A";

		if (hasValidSerial) {
			// Editing mode - serial already exists
			setIsEditing(true);
			setDirectSerialInput(existingSerial);
			if (hasValidModel) {
				setSelectedModel(existingModel);
			}
		} else {
			// New entry - set defaults
			setIsEditing(false);
			if (!hasValidModel) {
				// Set default model
				setSelectedModel(defaultModel);
				setFormValues(prev => ({
					...prev,
					"gen_gen_model": defaultModel
				}));
			} else {
				setSelectedModel(existingModel);
			}
		}
	}, []);

	// Sync with loaded serial number (handles async data loading)
	useLayoutEffect(() => {
		const existingSerial = formValues["gen_gen_sn"];
		const existingModel = formValues["gen_gen_model"];
		const hasValidSerial = existingSerial && existingSerial.length > 0 && existingSerial !== "N/A" && existingSerial !== "";
		const hasValidModel = existingModel && existingModel !== "N/A" && existingModel !== "";

		if (hasValidSerial) {
			setIsEditing(true);
			setDirectSerialInput(existingSerial);
			if (hasValidModel) {
				setSelectedModel(existingModel);
			}
		}
	}, [formValues["gen_gen_sn"], formValues["gen_gen_model"]]);

	// Re-initialize when switching to Flock Gen Trailer
	useEffect(() => {
		const unitModel = formValues["unit_identifiers_unit_model"];
		const existingModel = formValues["gen_gen_model"];

		// If unit model is Flock Gen Trailer and generator model is empty/N/A, set defaults
		if (unitModel === "Flock Gen Trailer" && (!existingModel || existingModel === "N/A" || existingModel === "")) {
			setIsEditing(false);
			setSelectedModel(defaultModel);
			setSelectedPrefix(null);
			setLastSixDigits("");
			setDirectSerialInput("");
			setFormValues(prev => ({
				...prev,
				"gen_gen_model": defaultModel,
				"gen_gen_model_Timestamp": new Date().toISOString(),
				"gen_gen_model_User": currentUser?.userinfo?.name || 'Unknown'
			}));
		}
	}, [formValues["unit_identifiers_unit_model"]]);

	// Update formValues when serial is complete (prefix + last 6 digits)
	useEffect(() => {
		if (!isEditing && selectedPrefix && lastSixDigits.length === 6) {
			const fullSerial = `${selectedPrefix}${lastSixDigits}`;
			setFormValues(prev => ({
				...prev,
				"gen_gen_sn": fullSerial,
				"gen_gen_sn_Timestamp": new Date().toISOString(),
				"gen_gen_sn_User": currentUser?.userinfo?.name || 'Unknown'
			}));
		}
	}, [selectedPrefix, lastSixDigits, isEditing]);

	// Handle model selection
	const handleModelSelect = (e, model) => {
		if (e) e.preventDefault();
		setSelectedModel(model);
		setSelectedPrefix(null);
		setLastSixDigits("");
		setFormValues(prev => ({
			...prev,
			"gen_gen_model": model,
			"gen_gen_model_Timestamp": new Date().toISOString(),
			"gen_gen_model_User": currentUser?.userinfo?.name || 'Unknown'
		}));
	};

	// Handle prefix selection
	const handlePrefixSelect = (e, prefix) => {
		if (e) e.preventDefault();
		setSelectedPrefix(prefix);
	};

	// Handle last 6 digits input
	const handleLastSixChange = (e) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 6);
		setLastSixDigits(value);
	};

	// Handle direct serial input (edit mode)
	const handleDirectSerialChange = (e) => {
		const value = e.target.value;
		setDirectSerialInput(value);
		setFormValues(prev => ({
			...prev,
			"gen_gen_sn": value,
			"gen_gen_sn_Timestamp": new Date().toISOString(),
			"gen_gen_sn_User": currentUser?.userinfo?.name || 'Unknown'
		}));
	};

	// Switch to create mode (reset serial)
	const handleReset = (e) => {
		if (e) e.preventDefault();
		setIsEditing(false);
		setSelectedPrefix(null);
		setLastSixDigits("");
		setDirectSerialInput("");
		setFormValues(prev => ({
			...prev,
			"gen_gen_sn": ""
		}));
	};

	const fullSerial = selectedPrefix && lastSixDigits.length === 6 ? `${selectedPrefix}${lastSixDigits}` : null;

	return (
		<div className="tw-flex tw-flex-col tw-gap-15px">
			{/* Model Selection */}
			<div className="tw-flex tw-flex-col tw-gap-5px">
				<label className="tw-text-sm tw-font-quaternary tw-font-quaternary-bold tw-uppercase tw-tracking-wider">
					Generator Model
				</label>
				<span className="tw-text-xs tw-opacity-70 tw-mb-5px">Select the generator model (2800i or 2800e)</span>
				<div className="tw-grid tw-grid-cols-2 -tw-gap-1px">
					{modelOptions.map((model) => (
						<button
							key={model}
							onClick={(e) => handleModelSelect(e, model)}
							className={`tw-min-h-40px tw-flex tw-items-center tw-justify-center tw-uppercase tw-font-quaternary tw-font-quaternary-bold tw-border-1px tw-border-solid ${
								selectedModel === model
									? 'tw-bg-white tw-text-black tw-pointer-events-none tw-cursor-default'
									: 'tw-bg-black tw-text-white tw-cursor-pointer hover:tw-bg-gray-800'
							}`}
						>
							{model}
						</button>
					))}
				</div>
				{formValues["gen_gen_model_User"] && (
					<span className="tw-mt-2px tw-opacity-80 tw-block tw-text-[.5em] tw-uppercase tw-tracking-wider tw-leading-none">
						Updated by: {formValues["gen_gen_model_User"] || 'N/A'} on {formValues["gen_gen_model_Timestamp"] && new Date(formValues["gen_gen_model_Timestamp"]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || 'N/A'}
					</span>
				)}
			</div>

			{/* Serial Number Section */}
			{selectedModel && (
				<div className="tw-flex tw-flex-col tw-gap-5px">
					<label className="tw-text-sm tw-font-quaternary tw-font-quaternary-bold tw-uppercase tw-tracking-wider">
						Generator Serial Number
					</label>

					{isEditing ? (
						/* Edit Mode - Direct Input */
						<div className="tw-flex tw-flex-col tw-gap-10px">
							<span className="tw-text-xs tw-opacity-70">Edit the serial number directly</span>
							<Input
								type="text"
								value={directSerialInput}
								onChange={handleDirectSerialChange}
								placeholder="Enter full serial number"
								className="tw-font-code tw-font-code-bold"
							/>
							<ButtonLight onClick={handleReset} className="tw-min-h-30px">
								<span className="tw-text-[.7em] tw-uppercase tw-font-quaternary tw-font-quaternary-bold tw-tracking-wide tw-px-10px">
									Reset &amp; Enter New Serial
								</span>
							</ButtonLight>
						</div>
					) : (
						/* Create Mode - Prefix + Last 6 */
						<div className="tw-flex tw-flex-col tw-gap-10px">
							{/* Prefix Selection */}
							<div className="tw-flex tw-flex-col tw-gap-5px">
								<span className="tw-text-xs tw-opacity-70">Select serial prefix</span>
								<div className="tw-grid tw-grid-cols-2 -tw-gap-1px">
									{prefixOptions.map((prefix) => (
										<button
											key={prefix}
											onClick={(e) => handlePrefixSelect(e, prefix)}
											className={`tw-min-h-30px tw-px-5px tw-flex tw-items-center tw-justify-center tw-font-code tw-font-code-bold tw-text-[.75em] tw-border-1px tw-border-solid ${
												selectedPrefix === prefix
													? 'tw-bg-white tw-text-black tw-pointer-events-none tw-cursor-default'
													: 'tw-bg-black tw-text-white tw-cursor-pointer hover:tw-bg-gray-800'
											}`}
										>
											{prefix}
										</button>
									))}
								</div>
							</div>

							{/* Last 6 Digits Input */}
							{selectedPrefix && (
								<div className="tw-flex tw-flex-col tw-gap-5px">
									<span className="tw-text-xs tw-opacity-70">Enter last 6 digits of serial</span>
									<Input
										type="tel"
										value={lastSixDigits}
										onChange={handleLastSixChange}
										placeholder="000000"
										maxLength={6}
										className="tw-font-code tw-font-code-bold tw-text-center tw-text-lg"
									/>
								</div>
							)}

							{/* Full Serial Preview */}
							{fullSerial && (
								<div className="tw-border-1px tw-border-solid tw-border-white/30 tw-text-white tw-text-center tw-flex tw-flex-col tw-p-10px tw-rounded-3px tw-bg-black/50">
									<span className="tw-font-quaternary tw-font-quaternary-normal tw-uppercase tw-text-[.6em] tw-tracking-[.2em] tw-opacity-70">
										Full Serial Number:
									</span>
									<span className="tw-font-code tw-font-code-bold tw-text-lg">{fullSerial}</span>
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{/* Timestamp info */}
			{formValues["gen_gen_sn_User"] && (
				<span className="tw-mt-2px tw-opacity-80 tw-block tw-text-[.5em] tw-uppercase tw-tracking-wider tw-leading-none">
					Updated by: {formValues["gen_gen_sn_User"] || 'N/A'} on {formValues["gen_gen_sn_Timestamp"] && new Date(formValues["gen_gen_sn_Timestamp"]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || 'N/A'}
				</span>
			)}
		</div>
	);
};

const PowerPanelSerialSelector = ({ field, formValues, setFormValues, isEditMode }) => {
	const { currentUser } = useAuth();
	const prefixOptions = [
		"24FS0",
		"25FSP",
		"25FFP",
		"FS-AF-00-26-",
		"FS-B0-00-26-",
		"FS-A0-00-26-",
		"FS-F0-01-26-",
		"FS-F0-00-26-",
		"FS-F0-01-26-",
		"FS-FG-01-26-"
	];

	const [selectedPrefix, setSelectedPrefix] = useState(null);
	const [remainingDigits, setRemainingDigits] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const [directSerialInput, setDirectSerialInput] = useState("");

	// Initialize on mount - check if editing existing serial
	useLayoutEffect(() => {
		const existingSerial = formValues[field.id];

		// Treat empty values as non-existing
		const hasValidSerial = existingSerial && existingSerial.length > 0 && existingSerial !== "N/A";

		if (hasValidSerial) {
			// Editing mode - serial already exists
			setIsEditing(true);
			setDirectSerialInput(existingSerial);
			// Try to detect prefix from existing serial
			const matchedPrefix = prefixOptions.find(p => existingSerial.startsWith(p));
			if (matchedPrefix) {
				setSelectedPrefix(matchedPrefix);
				setRemainingDigits(existingSerial.slice(matchedPrefix.length));
			}
		} else {
			// New entry
			setIsEditing(false);
		}
	}, []);

	// Sync with loaded serial number (handles async data loading)
	useLayoutEffect(() => {
		const existingSerial = formValues[field.id];
		const hasValidSerial = existingSerial && existingSerial.length > 0 && existingSerial !== "N/A" && existingSerial !== "";

		if (hasValidSerial) {
			setIsEditing(true);
			setDirectSerialInput(existingSerial);
			// Try to detect prefix from existing serial
			const matchedPrefix = prefixOptions.find(p => existingSerial.startsWith(p));
			if (matchedPrefix) {
				setSelectedPrefix(matchedPrefix);
				setRemainingDigits(existingSerial.slice(matchedPrefix.length));
			}
		}
	}, [formValues[field.id]]);

	// Update formValues when serial is complete (prefix + 4 digits)
	useEffect(() => {
		if (!isEditing && selectedPrefix && remainingDigits.length === 4) {
			const fullSerial = `${selectedPrefix}${remainingDigits}`;
			setFormValues(prev => ({
				...prev,
				[field.id]: fullSerial,
				[`${field.id}_Timestamp`]: new Date().toISOString(),
				[`${field.id}_User`]: currentUser?.userinfo?.name || 'Unknown'
			}));
		}
	}, [selectedPrefix, remainingDigits, isEditing]);

	// Handle prefix selection
	const handlePrefixSelect = (e, prefix) => {
		if (e) e.preventDefault();
		setSelectedPrefix(prefix);
	};

	// Handle remaining digits input (max 4 characters)
	const handleRemainingDigitsChange = (e) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 4);
		setRemainingDigits(value);
	};

	// Handle direct serial input (edit mode)
	const handleDirectSerialChange = (e) => {
		const value = e.target.value;
		setDirectSerialInput(value);
		setFormValues(prev => ({
			...prev,
			[field.id]: value,
			[`${field.id}_Timestamp`]: new Date().toISOString(),
			[`${field.id}_User`]: currentUser?.userinfo?.name || 'Unknown'
		}));
	};

	// Switch to create mode (reset serial)
	const handleReset = (e) => {
		if (e) e.preventDefault();
		setIsEditing(false);
		setSelectedPrefix(null);
		setRemainingDigits("");
		setDirectSerialInput("");
		setFormValues(prev => ({
			...prev,
			[field.id]: ""
		}));
	};

	const fullSerial = selectedPrefix && remainingDigits.length === 4 ? `${selectedPrefix}${remainingDigits}` : null;

	return (
		<div className="tw-flex tw-flex-col tw-gap-15px">
			{/* Serial Number Section */}
			<div className="tw-flex tw-flex-col tw-gap-5px">
				<label className="tw-text-sm tw-font-quaternary tw-font-quaternary-bold tw-uppercase tw-tracking-wider">
					<span dangerouslySetInnerHTML={{ __html: field.label }} />
				</label>
				{field.description && (
					<span className="tw-text-xs tw-opacity-70 tw-mb-5px" dangerouslySetInnerHTML={{ __html: field.description }} />
				)}

				{isEditing ? (
					/* Edit Mode - Direct Input */
					<div className="tw-flex tw-flex-col tw-gap-10px">
						<span className="tw-text-xs tw-opacity-70">Edit the serial number directly</span>
						<Input
							type="text"
							value={directSerialInput}
							onChange={handleDirectSerialChange}
							placeholder="Enter full serial number"
							className="tw-font-code tw-font-code-bold"
						/>
						<ButtonLight onClick={handleReset} className="tw-min-h-30px">
							<span className="tw-text-[.7em] tw-uppercase tw-font-quaternary tw-font-quaternary-bold tw-tracking-wide tw-px-10px">
								Reset &amp; Enter New Serial
							</span>
						</ButtonLight>
					</div>
				) : (
					/* Create Mode - Prefix + Remaining Digits */
					<div className="tw-flex tw-flex-col tw-gap-10px">
						{/* Prefix Selection */}
						<div className="tw-flex tw-flex-col tw-gap-5px">
							<span className="tw-text-xs tw-opacity-70">Select serial prefix</span>
							<div className="tw-grid tw-grid-cols-2 -tw-gap-1px">
								{prefixOptions.map((prefix) => (
									<button
										key={prefix}
										onClick={(e) => handlePrefixSelect(e, prefix)}
										className={`tw-min-h-30px tw-px-5px tw-flex tw-items-center tw-justify-center tw-font-code tw-font-code-bold tw-text-[.75em] tw-border-1px tw-border-solid ${
											selectedPrefix === prefix
												? 'tw-bg-white tw-text-black tw-pointer-events-none tw-cursor-default'
												: 'tw-bg-black tw-text-white tw-cursor-pointer hover:tw-bg-gray-800'
										}`}
									>
										{prefix}
									</button>
								))}
							</div>
						</div>

						{/* Remaining Digits Input */}
						{selectedPrefix && (
							<div className="tw-flex tw-flex-col tw-gap-5px">
								<span className="tw-text-xs tw-opacity-70">Enter last 4 digits of serial</span>
								<Input
									type="tel"
									value={remainingDigits}
									onChange={handleRemainingDigitsChange}
									placeholder="0000"
									maxLength={4}
									className="tw-font-code tw-font-code-bold tw-text-center tw-text-lg"
								/>
							</div>
						)}

						{/* Full Serial Preview */}
						{fullSerial && (
							<div className="tw-border-1px tw-border-solid tw-border-white/30 tw-text-white tw-text-center tw-flex tw-flex-col tw-p-10px tw-rounded-3px tw-bg-black/50">
								<span className="tw-font-quaternary tw-font-quaternary-normal tw-uppercase tw-text-[.6em] tw-tracking-[.2em] tw-opacity-70">
									Full Serial Number:
								</span>
								<span className="tw-font-code tw-font-code-bold tw-text-lg">{fullSerial}</span>
							</div>
						)}
					</div>
				)}
			</div>

			{/* Timestamp info */}
			{formValues[`${field.id}_User`] && (
				<span className="tw-mt-2px tw-opacity-80 tw-block tw-text-[.5em] tw-uppercase tw-tracking-wider tw-leading-none">
					Updated by: {formValues[`${field.id}_User`] || 'N/A'} on {formValues[`${field.id}_Timestamp`] && new Date(formValues[`${field.id}_Timestamp`]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || 'N/A'}
				</span>
			)}

			{/* Copy for VRM Setup button */}
			{(() => {
				const powerPanelSerial = formValues[field.id];
				const unitIdentifierName = formValues?.unit_identifiers_unit_identifier_name;
				const vin = formValues?.VIN;
				const vrmValue = powerPanelSerial && unitIdentifierName && vin
					? `Flex Security - ${unitIdentifierName} - ${powerPanelSerial} - ${vin}`
					: null;

				return (
					<div className="tw-mt-10px">
						<ButtonSecondary
							className={`tw-cursor-pointer tw-border-white/50 hover:!tw-bg-black hover:!tw-text-white tw-px-10px tw-py-2px tw-min-h-25px tw-mr-auto !tw-w-auto ${vrmValue ? '' : 'tw-opacity-50 !tw-pointer-events-none !tw-cursor-default'}`}
							disabled={!vrmValue}
							onClick={e => {
								e.preventDefault();
								navigator.clipboard.writeText(vrmValue || '').then(() => {
									alert(`Copied to clipboard: \n${vrmValue}`);
								}).catch(err => {
									alert('Failed to copy: ', err);
								});
							}}
						>
							<span className="tw-text-[.65em] tw-pointer-events-none tw-uppercase">Copy for VRM Setup</span>
						</ButtonSecondary>
					</div>
				);
			})()}
		</div>
	);
};

const EnlargeImage = ({ src, alt, onClick }) => {
	const [enlarged, setEnlarged] = useState(false);

	const handleClick = (e) => {
		e.preventDefault();
		setEnlarged(!enlarged);
	}

	return enlarged ? <>
		<div onClick={e => { handleClick(e) }} className="tw-cursor-pointer [&_*]:tw-select-none tw-overflow-clip tw-border-1px tw-border-solid tw-border-white/20 tw-radius-3px tw-shadow-sm tw-backdrop-blur-sm tw-aspect-[2/1] tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-black/90 tw-z-50 tw-flex tw-justify-center tw-items-center">
			<img src={src} alt={alt} className="tw-object-contain tw-max-w-[calc(100%-40px)] tw-max-h-[calc(100%-40px)]" />
			<div className="tw-fixed tw-top-10px tw-right-10px tw-bg-black/50 tw-p-2px tw-rounded-full">
				<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-6 tw-h-6 tw-text-white">
					<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
				</svg>
			</div>
		</div>
	</> : <>
		<div onClick={e => { handleClick(e) }} className="[&_*]:tw-select-none tw-cursor-pointer tw-overflow-clip tw-border-1px tw-border-solid tw-border-white/20 tw-radius-3px tw-shadow-sm tw-aspect-[2/1] md:tw-aspect-[1/1] tw-relative">
			<img src={src} alt={alt} className="tw-object-cover tw-w-full tw-h-full" />
			<span>
				<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-6 tw-h-6 tw-text-white tw-absolute tw-top-5px tw-right-5px tw-bg-black/50 tw-p-2px tw-rounded-full">
					<path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
				</svg>
			</span>
		</div>
	</>
}


const CameraModal = ({ isOpen, onClose, onCapture }) => {
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const streamRef = useRef(null);
	const [cameraError, setCameraError] = useState(null);
	const [facingMode, setFacingMode] = useState('environment'); // 'environment' = back camera, 'user' = front camera

	const startCamera = async (facing = facingMode) => {
		try {
			// Stop any existing stream first
			if (streamRef.current) {
				streamRef.current.getTracks().forEach(track => track.stop());
			}

			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: facing,
					width: { ideal: 1920 },
					height: { ideal: 1080 }
				},
				audio: false
			});

			streamRef.current = stream;
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
			}
			setCameraError(null);
		} catch (err) {
			console.error('Camera error:', err);
			if (err.name === 'NotAllowedError') {
				setCameraError('Camera access denied. Please allow camera access in your browser settings.');
			} else if (err.name === 'NotFoundError') {
				setCameraError('No camera found on this device.');
			} else {
				setCameraError('Unable to access camera. Please try again.');
			}
		}
	};

	const stopCamera = () => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach(track => track.stop());
			streamRef.current = null;
		}
	};

	const switchCamera = () => {
		const newFacing = facingMode === 'environment' ? 'user' : 'environment';
		setFacingMode(newFacing);
		startCamera(newFacing);
	};

	const capturePhoto = () => {
		if (!videoRef.current || !canvasRef.current) return;

		const video = videoRef.current;
		const canvas = canvasRef.current;
		const context = canvas.getContext('2d');

		// Set canvas size to match video
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		// Draw the video frame to canvas
		context.drawImage(video, 0, 0, canvas.width, canvas.height);

		// Convert canvas to blob
		canvas.toBlob((blob) => {
			if (blob) {
				const file = new File([blob], `camera_capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
				onCapture(file);
				handleClose();
			}
		}, 'image/jpeg', 0.9);
	};

	const handleClose = () => {
		stopCamera();
		onClose();
	};

	useEffect(() => {
		if (isOpen) {
			startCamera();
		} else {
			stopCamera();
		}

		return () => stopCamera();
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/90">
			<div className="tw-relative tw-w-full tw-max-w-2xl tw-mx-10px">
				{/* Close button */}
				<button
					type="button"
					onClick={handleClose}
					className="tw-absolute tw-top-10px tw-right-10px tw-z-10 tw-p-8px tw-bg-black/50 tw-rounded-full tw-text-white hover:tw-bg-black/70 tw-transition-all"
				>
					<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="tw-w-24px tw-h-24px">
						<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>

				{cameraError ? (
					<div className="tw-bg-black/50 tw-rounded-3px tw-p-40px tw-text-center">
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-60px tw-h-60px tw-mx-auto tw-mb-20px tw-opacity-50">
							<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M12 18.75H4.5a2.25 2.25 0 01-2.25-2.25V9m12.841 9.091L16.5 19.5m-1.409-1.409c.407-.407.659-.97.659-1.591v-9a2.25 2.25 0 00-2.25-2.25h-9c-.621 0-1.184.252-1.591.659m12.182 12.182L2.909 5.909M1.5 4.5l1.409 1.409" />
						</svg>
						<p className="tw-text-white tw-font-secondary-normal">{cameraError}</p>
						<button
							type="button"
							onClick={handleClose}
							className="tw-mt-20px tw-px-20px tw-py-10px tw-bg-white/20 hover:tw-bg-white/30 tw-rounded-3px tw-text-white tw-font-secondary-normal tw-transition-all"
						>
							Close
						</button>
					</div>
				) : (
					<>
						{/* Video preview */}
						<div className="tw-relative tw-bg-black tw-rounded-3px tw-overflow-hidden">
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								className="tw-w-full tw-h-auto tw-max-h-[70vh] tw-object-contain"
							/>
							{/* Hidden canvas for capture */}
							<canvas ref={canvasRef} className="tw-hidden" />
						</div>

						{/* Controls */}
						<div className="tw-flex tw-items-center tw-justify-center tw-gap-20px tw-mt-15px">
							{/* Switch camera button */}
							<button
								type="button"
								onClick={switchCamera}
								className="tw-p-12px tw-bg-white/20 hover:tw-bg-white/30 tw-rounded-full tw-text-white tw-transition-all"
								title="Switch camera"
							>
								<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-24px tw-h-24px">
									<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3" />
								</svg>
							</button>

							{/* Capture button - styled like phone camera shutter */}
							<button
								type="button"
								onClick={capturePhoto}
								className="!tw-w-70px !tw-h-70px tw-bg-transparent tw-rounded-full tw-border-4px tw-border-solid tw-border-white tw-p-4px hover:tw-scale-105 active:tw-scale-95 tw-transition-transform tw-flex tw-items-center tw-justify-center"
								title="Take photo"
							>
								<div className="!tw-w-full !tw-h-full tw-bg-white tw-rounded-full hover:tw-bg-white/90 tw-transition-colors" />
							</button>

							{/* Placeholder for symmetry */}
							<div className="tw-w-48px tw-h-48px"></div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

const ImageUpload = ({ formValues, setFormValues, field, currentUser }) => {
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState(null);
	const [dragActive, setDragActive] = useState(false);
	const [showCamera, setShowCamera] = useState(false);
	const [displayImageUrl, setDisplayImageUrl] = useState(null);
	const [imageLoading, setImageLoading] = useState(false);
	const fileInputRef = useRef(null);
	const { gcloudUploadImage, gcloudGetImage } = useGoogleStorageImage();

	const currentImageFilename = formValues[field.id];

	// Fetch the image from GCloud when there's a filename stored
	useEffect(() => {
		if (!currentImageFilename) {
			setDisplayImageUrl(null);
			return;
		}

		// If it's already a full URL (legacy data or blob), use it directly
		if (currentImageFilename.startsWith('http') || currentImageFilename.startsWith('blob:')) {
			setDisplayImageUrl(currentImageFilename);
			return;
		}

		// It's a filename, fetch from GCloud
		setImageLoading(true);
		gcloudGetImage(currentImageFilename, false)
			.then(result => {
				setDisplayImageUrl(result.imageUrl);
			})
			.catch(err => {
				console.error('Failed to load image:', err);
				setError('Failed to load image');
			})
			.finally(() => {
				setImageLoading(false);
			});
	}, [currentImageFilename]);

	const uploadImage = async (file) => {
		if (!file) return;

		// Validate file type
		if (!file.type.startsWith('image/')) {
			setError('Please select an image file');
			return;
		}

		// Validate file size (max 10MB)
		if (file.size > 10 * 1024 * 1024) {
			setError('Image must be smaller than 10MB');
			return;
		}

		setUploading(true);
		setError(null);

		try {
			// Rename file to include RVMP_QC prefix
			const extension = file.name.split('.').pop();
			const timestamp = Date.now();
			const newFileName = `RVMP_QC_${timestamp}.${extension}`;
			const renamedFile = new File([file], newFileName, { type: file.type });

			const formData = new FormData();
			formData.append('file', renamedFile);
			formData.append('fs', 'rvmp');

		const gCloudImage = await gcloudUploadImage(renamedFile);

		console.log('gCloudImage:', gCloudImage);

		// Store the filename (not blob URL) so it can be fetched later with auth
		const filename = gCloudImage.name;

			if (setFormValues) {
				const currentValue = formValues[field.id];
				const isValueChanging = currentValue !== filename;

				if (isValueChanging) {
					setFormValues(prev => ({
						...prev,
						[field.id]: filename,
						[`${field.id}_Timestamp`]: new Date().toISOString(),
						[`${field.id}_User`]: currentUser?.userinfo?.name || 'Unknown'
					}));
				}
			}

			// Set the display URL immediately from the upload response
			setDisplayImageUrl(gCloudImage.image.imageUrl);

			console.log('✓ Image uploaded successfully, stored filename:', filename);
		} catch (err) {
			console.error('Image upload error:', err);
			setError('Failed to upload image. Please try again.');
		} finally {
			setUploading(false);
		}
	};

	const handleDrag = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === "dragenter" || e.type === "dragover") {
			setDragActive(true);
		} else if (e.type === "dragleave") {
			setDragActive(false);
		}
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			uploadImage(e.dataTransfer.files[0]);
		}
	};

	const handleChange = (e) => {
		e.preventDefault();
		if (e.target.files && e.target.files[0]) {
			uploadImage(e.target.files[0]);
		}
	};

	const handleClick = () => {
		fileInputRef.current?.click();
	};

	const handleCameraClick = () => {
		setShowCamera(true);
	};

	const handleCameraCapture = (file) => {
		uploadImage(file);
	};

	return (
		<div className="tw-w-full tw-space-y-10px">
			{field.description && (
				<span className="tw-block tw-text-xs tw-opacity-70">{field.description}</span>
			)}

			<Label htmlFor={field.id}>{field.label}</Label>

			{currentImageFilename && (
				<div className="tw-mb-10px tw-border-1px tw-border-solid tw-border-white/20 tw-rounded-3px tw-overflow-hidden tw-bg-black/20">
					{imageLoading ? (
						<div className="tw-flex tw-items-center tw-justify-center tw-h-200px">
							<div className="tw-w-40px tw-h-40px tw-border-4px tw-border-solid tw-border-white/20 tw-border-t-white tw-rounded-full tw-animate-spin"></div>
						</div>
					) : displayImageUrl ? (
						<img
							src={displayImageUrl}
							alt={field.label}
							className="tw-w-full tw-h-auto tw-max-h-300px tw-object-contain"
						/>
					) : (
						<div className="tw-flex tw-items-center tw-justify-center tw-h-200px tw-text-white/50">
							Failed to load image
						</div>
					)}
					<div className="tw-p-10px tw-text-xs tw-opacity-70">
						Stored filename: {currentImageFilename}
					</div>
				</div>
			)}

			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				onChange={handleChange}
				className="tw-hidden"
				disabled={uploading}
			/>

			{/* Camera Modal */}
			<CameraModal
				isOpen={showCamera}
				onClose={() => setShowCamera(false)}
				onCapture={handleCameraCapture}
			/>

			{uploading ? (
				<div className="tw-border-2 tw-border-dashed tw-rounded-3px tw-p-20px tw-text-center tw-border-white/20 tw-bg-black/10">
					<div className="tw-flex tw-flex-col tw-items-center tw-gap-10px">
						<div className="tw-w-40px tw-h-40px tw-border-4px tw-border-solid tw-border-white/20 tw-border-t-white tw-rounded-full tw-animate-spin"></div>
						<p className="tw-text-sm tw-font-secondary-normal">Uploading image...</p>
					</div>
				</div>
			) : (
				<>
					{/* Drag and drop zone */}
					<div
						className={`tw-border-2 tw-border-dashed tw-rounded-3px tw-p-20px tw-text-center tw-transition-all ${
							dragActive
								? 'tw-border-primary tw-bg-primary/10'
								: 'tw-border-white/20 tw-bg-black/10'
						}`}
						onDragEnter={handleDrag}
						onDragLeave={handleDrag}
						onDragOver={handleDrag}
						onDrop={handleDrop}
					>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-30px tw-h-30px tw-opacity-50 tw-mx-auto tw-mb-5px">
							<path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
						</svg>
						<p className="tw-text-sm tw-font-secondary-normal tw-opacity-70">
							Drag and drop an image here
						</p>
					</div>

					{/* Action buttons */}
					<div className="tw-flex tw-gap-10px">
						<button
							type="button"
							onClick={handleClick}
							className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-8px tw-px-15px tw-py-12px tw-bg-white/10 hover:tw-bg-white/20 tw-border-1px tw-border-solid tw-border-white/20 tw-rounded-3px tw-transition-all tw-cursor-pointer"
						>
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-20px tw-h-20px">
								<path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
							</svg>
							<span className="tw-text-sm tw-font-secondary-normal">Upload File</span>
						</button>
						<button
							type="button"
							onClick={handleCameraClick}
							className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-8px tw-px-15px tw-py-12px tw-bg-white/10 hover:tw-bg-white/20 tw-border-1px tw-border-solid tw-border-white/20 tw-rounded-3px tw-transition-all tw-cursor-pointer"
						>
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="tw-w-20px tw-h-20px">
								<path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
								<path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
							</svg>
							<span className="tw-text-sm tw-font-secondary-normal">Take Photo</span>
						</button>
					</div>

					<p className="tw-text-xs tw-opacity-50 tw-text-center">PNG, JPG, GIF up to 10MB</p>
				</>
			)}

			{error && (
				<div className="tw-text-red-500 tw-text-sm tw-font-secondary-normal tw-mt-5px">
					{error}
				</div>
			)}

			{formValues[`${field.id}_User`] && (
				<span className="tw-mt-2px tw-opacity-80 tw-block tw-text-[.5em] tw-uppercase tw-tracking-wider tw-leading-none">
					Updated by: {formValues[`${field.id}_User`] || 'N/A'} on {formValues[`${field.id}_Timestamp`] && new Date(formValues[`${field.id}_Timestamp`]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || 'N/A'}
				</span>
			)}
		</div>
	);
};

const VinField = ({ vinsArrays, formValues, setFormValues, field, index, isEditMode, initialVIN, setHasDuplicateVIN }) => {
	const { currentUser } = useAuth();
	const { loadSheetAsJson, switchSpreadsheet, loadSpreadSheetByID, getCurrentSpreadsheetID } = useAppContext();
	const [vins, setVins] = useState(['1F9F1U410SA592230', '1F9F1U410SA592251', '1F9F1U410SA592265', '1F9F1U410SA592279', '1F9F1U410SA592282', '1F9F1U410SA592290', '1F9F1U410SA592301', '1F9F1U410SA592315', '1F9F1U410SA592329', '1F9F1U410SA592332', '1F9F1U410SA592346', '1F9F1U410SA592363', '1F9F1U410SA592377', '1F9F1U410SA592380', '1F9F1U410SA592394', '1F9F1U410SA592413', '1F9F1U410SA592427', '1F9F1U410SA592430', '1F9F1U410TA592431', '1F9F1U410TA592445', '1F9F1U410TA592459', '1F9F1U410TA592462', '1F9F1U410TA592476', '1F9F1U410TA592509', '1F9F1U410TA592512', '1F9F1U410TA592526', '1F9F1U410TA592543', '1F9F1U410TA592557', '1F9F1U410TA592560', '1F9F1U410TA592574', '1F9F1U410TA592588', '1F9F1U410TA592591', '1F9F1U410TA592607', '1F9F1U410TA592610', '1F9F1U410TA592624', '1F9F1U410TA592638', '1F9F1U410TA592641', '1F9F1U410TA592655', '1F9F1U410TA592669', '1F9F1U410TA592672', '1F9F1U410TA592686', '1F9F1U411SA592236', '1F9F1U411SA592257', '1F9F1U411SA592260', '1F9F1U411SA592274', '1F9F1U411SA592288', '1F9F1U411SA592296', '1F9F1U411SA592307', '1F9F1U411SA592310', '1F9F1U411SA592324', '1F9F1U411SA592338', '1F9F1U411SA592341', '1F9F1U411SA592355', '1F9F1U411SA592369', '1F9F1U411SA592372', '1F9F1U411SA592386', '1F9F1U411SA592405', '1F9F1U411SA592419', '1F9F1U411SA592422', '1F9F1U411TA592437', '1F9F1U411TA592440', '1F9F1U411TA592454', '1F9F1U411TA592468', '1F9F1U411TA592471', '1F9F1U411TA592485', '1F9F1U411TA592495', '1F9F1U411TA592504', '1F9F1U411TA592518', '1F9F1U411TA592521', '1F9F1U411TA592535', '1F9F1U411TA592549', '1F9F1U411TA592552', '1F9F1U411TA592566', '1F9F1U411TA592583', '1F9F1U411TA592597', '1F9F1U411TA592602', '1F9F1U411TA592616', '1F9F1U411TA592633', '1F9F1U411TA592647', '1F9F1U411TA592650', '1F9F1U411TA592664', '1F9F1U411TA592678', '1F9F1U411TA592681', '1F9F1U411TA592695', '1F9F1U412SA592231', '1F9F1U412SA592252', '1F9F1U412SA592266', '1F9F1U412SA592283', '1F9F1U412SA592291', '1F9F1U412SA592302', '1F9F1U412SA592316', '1F9F1U412SA592333', '1F9F1U412SA592347', '1F9F1U412SA592350', '1F9F1U412SA592364', '1F9F1U412SA592378', '1F9F1U412SA592381', '1F9F1U412SA592395', '1F9F1U412SA592400', '1F9F1U412SA592414', '1F9F1U412SA592428', '1F9F1U412TA592432', '1F9F1U412TA592446', '1F9F1U412TA592463', '1F9F1U412TA592477', '1F9F1U412TA592480', '1F9F1U412TA592490', '1F9F1U412TA592513', '1F9F1U412TA592527', '1F9F1U412TA592530', '1F9F1U412TA592544', '1F9F1U412TA592558', '1F9F1U412TA592561', '1F9F1U412TA592575', '1F9F1U412TA592589', '1F9F1U412TA592592', '1F9F1U412TA592608', '1F9F1U412TA592611', '1F9F1U412TA592625', '1F9F1U412TA592639', '1F9F1U412TA592642', '1F9F1U412TA592656', '1F9F1U412TA592673', '1F9F1U412TA592687', '1F9F1U412TA592690', '1F9F1U413SA592237', '1F9F1U413SA592258', '1F9F1U413SA592261', '1F9F1U413SA592275', '1F9F1U413SA592289', '1F9F1U413SA592297', '1F9F1U413SA592308', '1F9F1U413SA592311', '1F9F1U413SA592325', '1F9F1U413SA592339', '1F9F1U413SA592342', '1F9F1U413SA592356', '1F9F1U413SA592373', '1F9F1U413SA592387', '1F9F1U413SA592390', '1F9F1U413SA592406', '1F9F1U413SA592423', '1F9F1U413TA592438', '1F9F1U413TA592441', '1F9F1U413TA592455', '1F9F1U413TA592469', '1F9F1U413TA592472', '1F9F1U413TA592486', '1F9F1U413TA592496', '1F9F1U413TA592505', '1F9F1U413TA592519', '1F9F1U413TA592522', '1F9F1U413TA592536', '1F9F1U413TA592553', '1F9F1U413TA592567', '1F9F1U413TA592570', '1F9F1U413TA592584', '1F9F1U413TA592598', '1F9F1U413TA592603', '1F9F1U413TA592617', '1F9F1U413TA592620', '1F9F1U413TA592634', '1F9F1U413TA592648', '1F9F1U413TA592651', '1F9F1U413TA592665', '1F9F1U413TA592679', '1F9F1U413TA592680', '1F9F1U413TA592682', '1F9F1U413TA592696', '1F9F1U414SA592229', '1F9F1U414SA592232', '1F9F1U414SA592253', '1F9F1U414SA592267', '1F9F1U414SA592270', '1F9F1U414SA592284', '1F9F1U414SA592292', '1F9F1U414SA592303', '1F9F1U414SA592317', '1F9F1U414SA592320', '1F9F1U414SA592334', '1F9F1U414SA592348', '1F9F1U414SA592351', '1F9F1U414SA592365', '1F9F1U414SA592379', '1F9F1U414SA592382', '1F9F1U414SA592396', '1F9F1U414SA592401', '1F9F1U414SA592415', '1F9F1U414SA592429', '1F9F1U414TA592433', '1F9F1U414TA592447', '1F9F1U414TA592450', '1F9F1U414TA592464', '1F9F1U414TA592478', '1F9F1U414TA592481', '1F9F1U414TA592491', '1F9F1U414TA592500', '1F9F1U414TA592514', '1F9F1U414TA592528', '1F9F1U414TA592531', '1F9F1U414TA592545', '1F9F1U414TA592559', '1F9F1U414TA592562', '1F9F1U414TA592576', '1F9F1U414TA592593', '1F9F1U414TA592609', '1F9F1U414TA592612', '1F9F1U414TA592626', '1F9F1U414TA592643', '1F9F1U414TA592657', '1F9F1U414TA592660', '1F9F1U414TA592674', '1F9F1U414TA592688', '1F9F1U414TA592691', '1F9F1U415SA592238', '1F9F1U415SA592259', '1F9F1U415SA592262', '1F9F1U415SA592276', '1F9F1U415SA592298', '1F9F1U415SA592309', '1F9F1U415SA592312', '1F9F1U415SA592326', '1F9F1U415SA592343', '1F9F1U415SA592357', '1F9F1U415SA592360', '1F9F1U415SA592374', '1F9F1U415SA592388', '1F9F1U415SA592391', '1F9F1U415SA592407', '1F9F1U415SA592410', '1F9F1U415SA592424', '1F9F1U415TA592439', '1F9F1U415TA592442', '1F9F1U415TA592456', '1F9F1U415TA592473', '1F9F1U415TA592487', '1F9F1U415TA592497', '1F9F1U415TA592506', '1F9F1U415TA592523', '1F9F1U415TA592537', '1F9F1U415TA592540', '1F9F1U415TA592554', '1F9F1U415TA592568', '1F9F1U415TA592571', '1F9F1U415TA592585', '1F9F1U415TA592599', '1F9F1U415TA592604', '1F9F1U415TA592618', '1F9F1U415TA592621', '1F9F1U415TA592635', '1F9F1U415TA592649', '1F9F1U415TA592652', '1F9F1U415TA592666', '1F9F1U415TA592683', '1F9F1U415TA592697', '1F9F1U416SA592233', '1F9F1U416SA592254', '1F9F1U416SA592268', '1F9F1U416SA592271', '1F9F1U416SA592285', '1F9F1U416SA592293', '1F9F1U416SA592304', '1F9F1U416SA592318', '1F9F1U416SA592321', '1F9F1U416SA592335', '1F9F1U416SA592349', '1F9F1U416SA592352', '1F9F1U416SA592366', '1F9F1U416SA592383', '1F9F1U416SA592397', '1F9F1U416SA592402', '1F9F1U416SA592416', '1F9F1U416TA592434', '1F9F1U416TA592448', '1F9F1U416TA592451', '1F9F1U416TA592465', '1F9F1U416TA592479', '1F9F1U416TA592482', '1F9F1U416TA592492', '1F9F1U416TA592501', '1F9F1U416TA592515', '1F9F1U416TA592529', '1F9F1U416TA592532', '1F9F1U416TA592546', '1F9F1U416TA592563', '1F9F1U416TA592577', '1F9F1U416TA592580', '1F9F1U416TA592594', '1F9F1U416TA592613', '1F9F1U416TA592627', '1F9F1U416TA592630', '1F9F1U416TA592644', '1F9F1U416TA592658', '1F9F1U416TA592661', '1F9F1U416TA592675', '1F9F1U416TA592689', '1F9F1U416TA592692', '1F9F1U417SA592263', '1F9F1U417SA592277', '1F9F1U417SA592280', '1F9F1U417SA592299', '1F9F1U417SA592313', '1F9F1U417SA592327', '1F9F1U417SA592330', '1F9F1U417SA592344', '1F9F1U417SA592358', '1F9F1U417SA592361', '1F9F1U417SA592375', '1F9F1U417SA592389', '1F9F1U417SA592392', '1F9F1U417SA592408', '1F9F1U417SA592411', '1F9F1U417SA592425', '1F9F1U417TA592443', '1F9F1U417TA592457', '1F9F1U417TA592460', '1F9F1U417TA592474', '1F9F1U417TA592488', '1F9F1U417TA592498', '1F9F1U417TA592507', '1F9F1U417TA592510', '1F9F1U417TA592524', '1F9F1U417TA592538', '1F9F1U417TA592541', '1F9F1U417TA592555', '1F9F1U417TA592569', '1F9F1U417TA592572', '1F9F1U417TA592586', '1F9F1U417TA592605', '1F9F1U417TA592619', '1F9F1U417TA592622', '1F9F1U417TA592636', '1F9F1U417TA592653', '1F9F1U417TA592667', '1F9F1U417TA592670', '1F9F1U417TA592684', '1F9F1U417TA592698', '1F9F1U418SA592234', '1F9F1U418SA592255', '1F9F1U418SA592269', '1F9F1U418SA592272', '1F9F1U418SA592286', '1F9F1U418SA592294', '1F9F1U418SA592305', '1F9F1U418SA592319', '1F9F1U418SA592322', '1F9F1U418SA592336', '1F9F1U418SA592353', '1F9F1U418SA592367', '1F9F1U418SA592368', '1F9F1U418SA592370', '1F9F1U418SA592384', '1F9F1U418SA592398', '1F9F1U418SA592403', '1F9F1U418SA592417', '1F9F1U418SA592420', '1F9F1U418TA592435', '1F9F1U418TA592449', '1F9F1U418TA592452', '1F9F1U418TA592466', '1F9F1U418TA592483', '1F9F1U418TA592493', '1F9F1U418TA592502', '1F9F1U418TA592516', '1F9F1U418TA592533', '1F9F1U418TA592547', '1F9F1U418TA592550', '1F9F1U418TA592564', '1F9F1U418TA592578', '1F9F1U418TA592581', '1F9F1U418TA592595', '1F9F1U418TA592600', '1F9F1U418TA592614', '1F9F1U418TA592628', '1F9F1U418TA592631', '1F9F1U418TA592645', '1F9F1U418TA592659', '1F9F1U418TA592662', '1F9F1U418TA592676', '1F9F1U418TA592693', '1F9F1U419SA592264', '1F9F1U419SA592278', '1F9F1U419SA592281', '1F9F1U419SA592300', '1F9F1U419SA592314', '1F9F1U419SA592328', '1F9F1U419SA592331', '1F9F1U419SA592345', '1F9F1U419SA592359', '1F9F1U419SA592362', '1F9F1U419SA592376', '1F9F1U419SA592393', '1F9F1U419SA592409', '1F9F1U419SA592412', '1F9F1U419SA592426', '1F9F1U419TA592444', '1F9F1U419TA592458', '1F9F1U419TA592461', '1F9F1U419TA592475', '1F9F1U419TA592489', '1F9F1U419TA592499', '1F9F1U419TA592508', '1F9F1U419TA592511', '1F9F1U419TA592525', '1F9F1U419TA592539', '1F9F1U419TA592542', '1F9F1U419TA592556', '1F9F1U419TA592573', '1F9F1U419TA592587', '1F9F1U419TA592590', '1F9F1U419TA592606', '1F9F1U419TA592623', '1F9F1U419TA592637', '1F9F1U419TA592640', '1F9F1U419TA592654', '1F9F1U419TA592668', '1F9F1U419TA592671', '1F9F1U419TA592685', '1F9F1U419TA592699', '1F9F1U41XSA592235', '1F9F1U41XSA592256', '1F9F1U41XSA592273', '1F9F1U41XSA592287', '1F9F1U41XSA592295', '1F9F1U41XSA592306', '1F9F1U41XSA592323', '1F9F1U41XSA592337', '1F9F1U41XSA592340', '1F9F1U41XSA592354', '1F9F1U41XSA592371', '1F9F1U41XSA592385', '1F9F1U41XSA592399', '1F9F1U41XSA592404', '1F9F1U41XSA592418', '1F9F1U41XSA592421', '1F9F1U41XTA592436', '1F9F1U41XTA592453', '1F9F1U41XTA592467', '1F9F1U41XTA592470', '1F9F1U41XTA592484', '1F9F1U41XTA592494', '1F9F1U41XTA592503', '1F9F1U41XTA592517', '1F9F1U41XTA592520', '1F9F1U41XTA592534', '1F9F1U41XTA592548', '1F9F1U41XTA592551', '1F9F1U41XTA592565', '1F9F1U41XTA592579', '1F9F1U41XTA592582', '1F9F1U41XTA592596', '1F9F1U41XTA592601', '1F9F1U41XTA592615', '1F9F1U41XTA592629', '1F9F1U41XTA592632', '1F9F1U41XTA592646', '1F9F1U41XTA592663', '1F9F1U41XTA592677', '1F9F1U41XTA592680', '1F9F1U41XTA592694']);
	const [isDuplicateVIN, setIsDuplicateVIN] = useState(false);

	// Check for duplicate VIN
	useEffect(() => {
		if (formValues?.VIN && formValues?.VIN?.length === 17 && vinsArrays && vinsArrays.length > 0) {
			// In edit mode, allow the original VIN but check for duplicates of other VINs
			const isDuplicate = vinsArrays.includes(formValues.VIN) && (!isEditMode || formValues.VIN !== initialVIN);
			setIsDuplicateVIN(isDuplicate);
			// Notify parent component about duplicate VIN status
			if (setHasDuplicateVIN) {
				setHasDuplicateVIN(isDuplicate);
			}
		} else {
			setIsDuplicateVIN(false);
			if (setHasDuplicateVIN) {
				setHasDuplicateVIN(false);
			}
		}
	}, [formValues?.VIN, vinsArrays, isEditMode, initialVIN, setHasDuplicateVIN]);

	useEffect(() => {
		if (formValues?.VIN && formValues?.VIN?.length === 17) {
			// Use selected unit model or default to "Flock Gen Trailer"
			const unitModel = formValues?.unit_identifiers_unit_model || "Flock Gen Trailer";
			const last6 = formValues?.VIN?.slice(-6);
			const unitIdentifierName = `${unitModel} ${last6}`;

			// Only update unit identifier name if it doesn't match the expected value
			if (formValues?.unit_identifiers_unit_identifier_name !== unitIdentifierName) {
				setFormValues(prev => ({
					...prev,
					unit_identifiers_unit_identifier_name: unitIdentifierName,
					unit_identifiers_unit_identifier_name_Timestamp: new Date().toISOString(),
					unit_identifiers_unit_identifier_name_User: currentUser?.userinfo?.name || 'Unknown'
				}));
			}
		}
	}, [formValues?.VIN, formValues?.unit_identifiers_unit_model, formValues?.unit_identifiers_unit_identifier_name]);


	const handleFormItem = (e) => {
		if (setFormValues) {
			// Only update timestamp if the value is actually changing
			const currentValue = formValues[e.target.id];
			const newValue = e.target.value.toUpperCase();
			const isValueChanging = currentValue !== newValue;

			if (isValueChanging) {
				setFormValues(prev => ({
					...prev,
					[e.target.id]: newValue,
					[`${e.target.id}_Timestamp`]: new Date().toISOString(),
					[`${e.target.id}_User`]: currentUser.userinfo ? currentUser.userinfo?.name : 'Unknown'
				}));
			} else {
				// Value hasn't changed, just set the value without updating timestamp
				setFormValues(prev => ({
					...prev,
					[e.target.id]: newValue
				}));
			}
		}
	}

	return vins && vins.length && <>
		<Field
			key={`${field.id}_${index}2`}
			type={field.type || 'text'}
			className="tw-flex-grow"
			initValue={formValues[field.id] || field.defaultValue || null}
			value={formValues[field.id] || field.defaultValue || ''}
			id={field.id}
			label={`<span>${field.label}</span>`}
			placeholder={field.placeholder}
			description={field.description || null}
			onChange={e => handleFormItem(e)}
			datalist={vins && vins.length > 0 ? vins : null}
			defaultValue={formValues[field.id] || field.defaultValue || null}
			disabled={isEditMode}
		/>
		{isDuplicateVIN && (
			<div className="tw-mb-5 tw-text-red-500 tw-text-sm tw-font-secondary-normal">
				This VIN number has already been used for another unit. Please verify and enter a unique VIN.
			</div>
		)}
	</>
}



const QItem = ({ vinsArrays, slug, categoryNumber, cqc, sectionNumber, category, section, name, description, images, number, formValues, setFormValues, initialLoadValues, form, hideCheckbox, isEditMode, setHasDuplicateVIN }) => {
	const { currentUser } = useAuth();

	const handleFormItem = (e) => {
		if (setFormValues) {
			// Only update timestamp if the value is actually changing
			const currentValue = formValues[e.target.id];
			const isValueChanging = currentValue !== e.target.value;

			if (isValueChanging) {
				setFormValues(prev => ({
					...prev,
					[e.target.id]: e.target.value,
					[`${e.target.id}_Timestamp`]: new Date().toISOString(),
					[`${e.target.id}_User`]: currentUser.userinfo ? currentUser.userinfo?.name : 'Unknown'
				}));
			} else {
				// Value hasn't changed, just set the value without updating timestamp
				setFormValues(prev => ({
					...prev,
					[e.target.id]: e.target.value
				}));
			}
		}
	}


	return (
		<div id={removeSpecialCharacters(category + "-" + section.name + "-" + name)} className="tw-border-1px tw-border-solid tw-p-20d tw-rounded-3px tw-overflow-clip tw-bg-black/5 tw-shadow-md tw-border-white/5">
			<div>
				<h4 className="tw-text-2xl tw-font-secondary-light tw-tracking-widest tw-uppercase tw-mb-10d">
					Item {categoryNumber}.{sectionNumber}.{number}
				</h4>
			</div>
			<div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-gap-20d tw-mb-10d">
				{images && images.length > 0 && <div className="md:tw-max-w-200px md:tw-min-w-50px  tw-flex-shrink-0 tw-aspect-[2/1] tw-min-h-100px tw-overflow-clip tw-border-1px tw-border-solid tw-rounded-6px tw-shadow-sm">
					{images.map((img, i) => <EnlargeImage key={img + i} src={`/images/${img}`} alt={name.replaceAll('"', "'") + (images.length > 1 ? ` image ${i + 1}` : '')} />)}
				</div>}
				<div className="tw-flex-grow">
					{name && <h5 className="tw-text-xl tw-font-secondary-normal tw-tracking-wide tw-uppercase tw-mb-0 tw-mt-0p tw-leading-[1.2]" dangerouslySetInnerHTML={{ __html: name }}></h5>}
					{ cqc ? <>
						<span className="tw-block tw-w-full tw-text-[.6em] tw-font-quaternary tw-font-quaternary-bold tw-opacity-70 tw-mt-2px tw-mb-5px">
							{ `CQC Reference: ${ cqc }` }
						</span>
					</> : <></> }
					{description && description.length > 0 && <div className="tw-flex tw-w-full tw-gap-10d">
						<p className="tw-leading-[1.3] tw-flex-grow tw-mb-10d tw-w-full tw-font-secondary tw-font-light tw-text-sm tw-text-white/70 tw-flex tw-flex-col [&_>*]:tw-w-full tw-space-y-5p">
							{description.map((item, index) => <span key={item + index} dangerouslySetInnerHTML={{ __html: item }} />)}
						</p>
					</div>}

					{form && form.length > 0 && <>
						<div className="tw-space-y-15px">
							{form && form.length > 0 && form
								.filter(field => {
									// Hide cqc-gen items if unit model is not "Flock Gen Trailer"
									if (field.cqc === 'cqc-gen' && formValues?.unit_identifiers_unit_model !== "Flock Gen Trailer") {
										return false;
									}
									return true;
								})
								.map((field, index) => (
								<div key={index} className="tw-flex tw-flex-col">
									<div key={`${field.id}_${index}_2`} className="tw-w-full tw-flex tw-flex-col">
										{field.overrideComponent ? <>
											{field.overrideComponent === "vin" ? <>
												<VinField
													vinsArrays={vinsArrays}
													formValues={formValues}
													setFormValues={setFormValues}
													field={field}
													index={index}
													isEditMode={isEditMode}
													initialVIN={initialLoadValues?.VIN}
													setHasDuplicateVIN={setHasDuplicateVIN}
												/>
											</> : field.overrideComponent === "unitModel" ? <>
												<UnitModelSelector
													field={field}
													formValues={formValues}
													setFormValues={setFormValues}
												/>
											</> : field.overrideComponent === "generatorSerial" ? <>
												<GeneratorSerialSelector
													field={field}
													formValues={formValues}
													setFormValues={setFormValues}
													isEditMode={isEditMode}
												/>
											</> : field.overrideComponent === "powerPanelSerial" ? <>
												<PowerPanelSerialSelector
													field={field}
													formValues={formValues}
													setFormValues={setFormValues}
													isEditMode={isEditMode}
												/>
											</> : field.overrideComponent === "image" ? <>
												<ImageUpload
													formValues={formValues}
													setFormValues={setFormValues}
													field={field}
													currentUser={currentUser}
												/>
											</> : <>
												<Field
													key={`${field.id}_${index}2`}
													type={field.type || 'text'}
													className="tw-flex-grow"
													initValue={formValues[field.id] || field.defaultValue || null}
													value={formValues[field.id] || field.defaultValue || ''}
													id={field.id}
													label={`<span>${field.label}</span>`}
													placeholder={field.placeholder}
													cqc={cqc || null}
													description={field.description || null}
													onChange={e => handleFormItem(e)}
													defaultValue={formValues[field.id] || field.defaultValue || null}
												/>
											</>
											}
										</> : <>
											<Field
												key={`${field.id}_${index}2`}
												type={field.type || 'text'}
												className="tw-flex-grow"
												initValue={formValues[field.id] || field.defaultValue || null}
												value={formValues[field.id] || field.defaultValue || ''}
												id={field.id}
												label={`<span>${field.label}</span>`}
												placeholder={field.placeholder}
												cqc={ cqc || null}
												description={field.description || null}
												onChange={e => handleFormItem(e)}
												defaultValue={formValues[field.id] || field.defaultValue || null}
											/>
										</>}
									</div>

									{/* Skip "Updated by" for override components - they render their own */}
									{!field.overrideComponent && formValues[`${field.id}_User`] ? <span className={`tw-mt-2px tw-opacity-80 tw-block tw-text-[.5em] tw-uppercase tw-tracking-wider tw-leading-none`}>Updated by: {formValues[`${field.id}_User`] || 'N/A'} on {formValues[`${field.id}_Timestamp`] && new Date(formValues[`${field.id}_Timestamp`]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }) || 'N/A'}</span> : <></>}
								</div>
							))}
						</div>
					</>}

				</div>
			</div>

			{!hideCheckbox && !form?.some(field => field.overrideComponent === "image") && <div>
				<CheckBox slug={slug} formValues={formValues} setFormValues={setFormValues} name={name} cqc={cqc || null} initialLoadValues={initialLoadValues} />
			</div>}
		</div>
	);
};


// compare two objects, ignoring _Timestamp and _User fields to return true if they are different, false if they are the same
const compareObjects = (obj1, obj2) => {
	const filteredObj1 = Object.fromEntries(
		Object.entries(obj1).filter(([key]) => !key.endsWith("_Timestamp") && !key.endsWith("_User"))
	);
	const filteredObj2 = Object.fromEntries(
		Object.entries(obj2).filter(([key]) => !key.endsWith("_Timestamp") && !key.endsWith("_User"))
	);
	return JSON.stringify(filteredObj1) !== JSON.stringify(filteredObj2);
};

// used to compare two objects and return only the fields that are different while ignoring _Timestamp and _User fields
const getDifferences = (obj1, obj2) => {
	const diffs = {};
	for (const key in obj2) {
		if (key.endsWith("_Timestamp") || key.endsWith("_User")) continue;
		if (obj2[key] !== obj1[key]) {
			diffs[key] = obj2[key];
		}
	}
	return diffs;
};

// this function is used on saving, to only send the fields that were edited + their _Timestamp and _User fields
const getEditedFields = (obj1, obj2) => {
	const diffs = {};
	for (const key in obj2) {
		if (key.endsWith("_Timestamp") || key.endsWith("_User")) continue;
		if (obj2[key] !== obj1[key]) {
			diffs[key] = obj2[key];
			if (obj2[`${key}_Timestamp`]) diffs[`${key}_Timestamp`] = obj2[`${key}_Timestamp`];
			if (obj2[`${key}_User`]) diffs[`${key}_User`] = obj2[`${key}_User`];
		}
	}
	return diffs;
};


const extractSlugsAndCreateFields = (data) => {
	const slugs = [];

	const recurseSections = (categories) => {
		categories?.forEach(category => {
			category?.sections?.forEach(section => {
				section?.items?.forEach(item => {
					// handle form fields first
					item?.form?.forEach(formField => {
						slugs.push(formField.id);
						slugs.push(`${formField.id}_Timestamp`);
						slugs.push(`${formField.id}_User`);
					}
					);
					slugs.push(item.slug);
					slugs.push(`${item.slug}_Timestamp`);
					slugs.push(`${item.slug}_User`);
					slugs.push(`${item.slug}_Details`);
					slugs.push(`${item.slug}_Details_Timestamp`);
					slugs.push(`${item.slug}_Details_User`);
				}
				);
				if (section?.sections) {
					recurseSections(section.sections);
				}
			}
			);
		}
		);
	}

	recurseSections(data);

	return slugs.join(", ");
	// return slugs.join(", ") + ", " + slugs.map(slug => `${slug}_Details, ${slug}_User, ${slug}_Timestamp`).join(", ");
}



export default function FSQC2() {
	const { currentUser } = useAuth();
	const [initialLoadValues, setInitialLoadValues] = useState({});
	const [formValues, setFormValues] = useState({});
	const [itemsChanged, setItemsChanged] = useState(false);
	const { addRowToSheet, loadSheetAsJson, getObjectByUUID, updateRowInSheet, saveSuccess, loadSpreadSheetByID, getCurrentSpreadsheetID } = useAppContext();
	const [isEditMode, setIsEditMode] = useState(false);
	const [unitId, setUnitId] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedStation, setSelectedStation] = useState("all");
	const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
	const [filterSnapshot, setFilterSnapshot] = useState({});
	const [vinsArrays, setVinsArrays] = useState(null);
	const [missingKeys, setMissingKeys] = useState(null);
	const [showNeedToComplete, setShowNeedToComplete] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [hasDuplicateVIN, setHasDuplicateVIN] = useState(false);
	const [hideFilterBar, setHideFilterBar] = useState(false);

	const { categoryNavOpen, setCategoryNavOpen } = useCategoryNav();
	const params = useParams();
	const paramId = params.id;
	const paramVersion = params.version || 'v1'; // Default to v1 if not specified
	const [accessFilteredCategories, setAccessFilteredCategories] = useState([]);

	// Get the PDI version data
	// PDI.flockTrailer is an array: [{ v1: [...] }, { v2: [...] }]
	const pdiVersionObj = PDI?.flockTrailer?.find(obj => obj[paramVersion]) || PDI?.flockTrailer?.[0];
	const pdiVersion = pdiVersionObj?.[paramVersion] || pdiVersionObj?.v1 || [];

	// Check if version is greater than v3 (i.e., not v1, v2, or v3)
	const isVersionAboveV3 = !['v1', 'v2', 'v3'].includes(paramVersion);

	// Collect all required field IDs and labels from the PDI data (for versions > v3)
	const requiredFields = isVersionAboveV3 ? pdiVersion?.flatMap(category =>
		category.sections?.flatMap(section =>
			section.items?.flatMap(item =>
				(item.form || [])
					.filter(field => field.required && field.id !== 'final_signoff_user')
					.map(field => ({ id: field.id, label: field.label }))
			) || []
		) || []
	) || [] : [];

	// Check which required fields are incomplete
	const incompleteRequiredFields = requiredFields.filter(field => {
		const value = formValues[field.id];
		return !value || value === '';
	});

	const hasIncompleteRequiredFields = incompleteRequiredFields.length > 0;

	// Get unique stations from access-filtered categories
	const availableStations = [...new Set(
		accessFilteredCategories?.flatMap(category =>
			category.sections?.flatMap(section =>
				section.items?.flatMap(item => item.stations || [])
			) || []
		) || []
	)].sort((a, b) => a - b);

	// Helper function to check if an item is incomplete
	// Uses filterSnapshot instead of live formValues to prevent items from disappearing while editing
	const isItemIncomplete = (item) => {
		// Use snapshot for filtering, fallback to formValues if no snapshot exists
		const valuesToCheck = showIncompleteOnly && Object.keys(filterSnapshot).length > 0 ? filterSnapshot : formValues;

		// Check if item slug (Yes/No checkbox) is marked "No" or empty
		if (!item.hideCheckbox) {
			const slugValue = valuesToCheck[item.slug];
			if (!slugValue || slugValue === "No") {
				return true;
			}
		}

		// Check if any form fields are empty
		if (item.form && item.form.length > 0) {
			for (const formField of item.form) {
				const fieldValue = valuesToCheck[formField.id];
				if (!fieldValue || fieldValue === "") {
					return true;
				}
			}
		}

		return false;
	};

	// Helper function to check if an item matches the search term
	const matchesSearch = (item, section, category) => {
		if (!searchTerm || searchTerm.trim() === "") return true;

		const search = searchTerm.toLowerCase().trim();

		// Search in category name
		if (category.category?.toLowerCase().includes(search)) return true;

		// Search in section name
		if (section.name?.toLowerCase().includes(search)) return true;

		// Search in item name/title
		if (item.name?.toLowerCase().includes(search)) return true;

		// Search in form field labels
		if (item.form && item.form.length > 0) {
			const hasMatchingFormField = item.form.some(field =>
				field.label?.toLowerCase().includes(search) ||
				field.placeholder?.toLowerCase().includes(search)
			);
			if (hasMatchingFormField) return true;
		}

		// Search for checkbox values (Yes/No/N/A) by item name
		const checkboxValues = ["yes", "no", "n/a"];
		if (checkboxValues.includes(search) && !item.hideCheckbox) {
			// Show all items with checkboxes when searching for checkbox values
			return true;
		}

		return false;
	};

	// Apply station filter, incomplete filter, and search filter on top of access-filtered categories
	const filteredCategories = accessFilteredCategories?.map((category) => {
		const filteredSections = category.sections?.map((section) => {
			const filteredItems = section.items?.filter(item => {
				// Station filter
				if (selectedStation !== "all") {
					const stationNum = parseInt(selectedStation);
					if (!item.stations?.includes(stationNum)) {
						return false;
					}
				}

				// Incomplete items filter
				if (showIncompleteOnly) {
					if (!isItemIncomplete(item)) {
						return false;
					}
				}

				// Search filter
				if (!matchesSearch(item, section, category)) {
					return false;
				}

				return true;
			});
			return filteredItems && filteredItems.length > 0 ? { ...section, items: filteredItems } : null;
		}).filter(Boolean);

		return filteredSections && filteredSections.length > 0 ? { ...category, sections: filteredSections } : null;
	}).filter(Boolean);

	useEffect(() => {
		if (missingKeys && missingKeys.length > 0 && showNeedToComplete === true ) {
			missingKeys.forEach(key => {
				console.log(`Missing key: ${key}`);
				console.log(comparePDIToKeys(formValues))
				document.querySelectorAll(`div[id="${ key }"]`, `input[id="${ key }"]`).forEach(item => {
					item.classList.add("tw-border-10px");
					item.classList.add("tw-border-red-600");
					console.log(item)
				})
			});
		}
	}, [missingKeys, itemsChanged, showNeedToComplete]);

	useEffect(() => {
		if (formValues && initialLoadValues) {
			setItemsChanged(compareObjects(initialLoadValues, formValues));
		}

		if (formValues && itemsChanged && formValues?.unit_identifiers_unit_identifier_name !== "Unit") {
			setMissingKeys(comparePDIToKeys(formValues)?.missingKeys || null);
		}
		console.log("Form values changed:", formValues);
	}, [formValues]);

	// Filter categories based on user access
	useEffect(() => {
		if (pdiVersion && currentUser?.userinfo?.name) {
			const filtered = filterCategories(pdiVersion, currentUser.userinfo.name);
			setAccessFilteredCategories(filtered);
		} else {
			setAccessFilteredCategories(pdiVersion || []);
		}
	}, [currentUser, PDI, pdiVersion]);


	const handleSave = async (e) => {
		// Prevent default form submission if coming from a submit event
		e?.preventDefault?.();

		// Compute diffs in parallel
		const [onlyEditedFields, onlyEditedFieldsWithTimestamps] = await Promise.all([
			getDifferences(initialLoadValues, formValues),
			getEditedFields(initialLoadValues, formValues),
		]);

		// UI state: show loading on the triggering button (if any)
		const btn = e?.currentTarget ?? null;
		btn?.classList.add("_loading");
		setIsSaving(true);

		// One timestamp for this operation
		const nowISO = new Date().toISOString();
		const userName = currentUser?.userinfo?.name || "Unknown";
		const company = formValues?.Company || "Flock";
		const UUID = formValues?.UUID || unitId || crypto.randomUUID();

		try {
			// ===== Edit mode: partial update of an existing row =====
			const hasEdits =
				!!onlyEditedFields &&
				typeof onlyEditedFields === "object" &&
				Object.keys(onlyEditedFields).length > 0;

			if (isEditMode && hasEdits && onlyEditedFieldsWithTimestamps) {
				const latestRows = await loadSheetAsJson(true);
				const latestObject = getObjectByUUID(latestRows, unitId) ?? {};

				const payload = {
					...latestObject,
					UUID: UUID,
					Company: company,
					"Updated By": userName,
					"Updated On": nowISO,
					"Version": paramVersion,
					...onlyEditedFieldsWithTimestamps,
				};

				await updateRowInSheet(payload, "Updated row");

				// Pull fresh data again after the update to reflect what’s truly stored
				const rowsAfter = await loadSheetAsJson(true);
				const fresh = getObjectByUUID(rowsAfter, unitId) ?? payload;

				// Update local state: make "fresh" the new truth
				setFormValues({ ...fresh });
				setInitialLoadValues({ ...fresh });

				// Update filter snapshot if incomplete filter is active
				if (showIncompleteOnly) {
					setFilterSnapshot({ ...fresh });
				}

				// UX cleanup
				saveSuccess(btn);
				setIsEditMode(true);     // stay in edit mode
				setItemsChanged(false);  // hide save button, reset changed state

				return;
			}

			// ===== Create mode: add a brand new row =====
			setUnitId(UUID);

			const createPayload = {
				UUID: UUID,
				Company: company,
				"Created By": userName,
				"Created On": nowISO,
				"Updated By": userName,
				"Updated On": nowISO,
				"Version": paramVersion,
				...formValues,
			};

			await addRowToSheet(createPayload);

			// Fetch what the sheet actually stored
			const rows = await loadSheetAsJson(true);
			const fresh = getObjectByUUID(rows, UUID) ?? createPayload;

			// Update local state
			setFormValues({ ...fresh });
			setInitialLoadValues({ ...fresh });
			setItemsChanged(false);
			setIsEditMode(true);

			// Update filter snapshot if incomplete filter is active
			if (showIncompleteOnly) {
				setFilterSnapshot({ ...fresh });
			}

			saveSuccess(btn);
			window.history.replaceState({}, document.title, `/fs/flock-edit/${UUID}/${paramVersion}`);
		} catch (err) {
			console.error("Save failed:", err);
			alert(isEditMode ? "Your updates failed - It did not save!" : "Saving your changes failed!");
		} finally {
			// Always clear saving state and button loading class
			setIsSaving(false);
			btn?.classList.remove("_loading");
		}
	};


	const scrollToCategory = (categoryIndex) => {
		const categoryId = `category-${categoryIndex}`;
		const element = document.getElementById(categoryId);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			setCategoryNavOpen(false);
		}
	}

	const scrollToSection = (categoryIndex, sectionIndex) => {
		const sectionId = `category-${categoryIndex}-section-${sectionIndex}`;
		const element = document.getElementById(sectionId);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			setCategoryNavOpen(false);
		}
	}

	useLayoutEffect(() => {
		// Wait for currentUser AND credentials to be available before fetching data
		if (!currentUser || !currentUser.credentials || !currentUser.credentials.access_token) {
			return;
		}

		setIsLoading(true);

		if (isEditMode && unitId) {
			loadSheetAsJson().then((rows) => {
				const object = getObjectByUUID(rows, unitId);

				setInitialLoadValues(object || {});
				setFormValues(object || {});
			}).catch((error) => {
				console.error('Failed to load unit data:', error);
			}).finally(() => {
				setIsLoading(false);
			});
		} else {
			// get Default Values from PDI and add to setFormValues()
			if (PDI && currentUser) {
				const defaults = {};
				const nowISO = new Date().toISOString();
				const userName = currentUser?.userinfo?.name || "Unknown";

				loadSheetAsJson().then((rows) => {
					// get all VINs from rows
					const vins = rows.map(row => row.VIN).filter(vin => vin && vin.length === 17);
					if (vins.length > 0) setVinsArrays(vins);
				}).catch((error) => {
					console.error('Failed to load VIN data:', error);
				}).finally(() => {
					setIsLoading(false);
				});

				const recurseSectionsForDefaults = (categories) => {
					categories?.forEach(category => {
						category?.sections?.forEach(section => {
							section?.items?.forEach(item => {
								// handle form fields first
								item?.form?.forEach(formField => {
									if (formField.defaultValue) {
										defaults[formField.id] = formField.defaultValue;
										defaults[`${formField.id}_Timestamp`] = nowISO;
										defaults[`${formField.id}_User`] = userName;
									}
								}
								);
								if (item.defaultValue) {
									defaults[item.slug] = item.defaultValue;
									defaults[`${item.slug}_Timestamp`] = nowISO;
									defaults[`${item.slug}_User`] = userName;
								}
							}
							);
							if (section?.sections) {
								recurseSectionsForDefaults(section.sections);
							}
						}
						);
					}
					);
				}
				recurseSectionsForDefaults(pdiVersion);
				setFormValues(defaults);
				setInitialLoadValues({}); // Set to empty object so defaults are detected as changes
			} else {
				setIsLoading(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUser, isEditMode, unitId]);

	useLayoutEffect(() => {
		if (paramId) {
			setIsEditMode(true);
			setUnitId(paramId);
		} else {
			setIsEditMode(false);
			setUnitId(null);
			setItemsChanged(false);
		}
	}, [paramId]);

	useEffect(() => {
		if (itemsChanged) {
			const handleBeforeUnload = (e) => {
				e.preventDefault();
				e.returnValue = '';
			};

			window.addEventListener('beforeunload', handleBeforeUnload);
			return () => {
				window.removeEventListener('beforeunload', handleBeforeUnload);
			}
		}
	}, [itemsChanged, formValues]);

	// Hide filter bar when user scrolls to first category
	useEffect(() => {
		const handleScroll = () => {
			const firstCategory = document.getElementById('category-0');
			if (firstCategory) {
				const rect = firstCategory.getBoundingClientRect();
				// Hide filter bar when first category reaches the top of the viewport
				setHideFilterBar(rect.top <= 0);
			}
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useLayoutEffect(() => {
		if (PDI) {
			// console.clear();
			// const pdiVersionObj = PDI?.flockTrailer?.find(obj => obj[paramVersion]) || PDI?.flockTrailer?.[0];
			// const pdiData = pdiVersionObj?.[paramVersion] || pdiVersionObj?.v1 || [];
			// console.log(extractSlugsAndCreateFields(pdiData));
		}
	}, [PDI, paramVersion]);

	return <>
		<div id="filter-bar" className={`tw-sticky tw-top-0 tw-z-2 tw-bg-black tw-p-10px tw-mb-20px tw-rounded-3px tw-shadow-lg tw-transition-all tw-duration-300 ${hideFilterBar ? 'tw-opacity-0 tw-pointer-events-none -tw-translate-y-full' : 'tw-opacity-100 tw-pointer-events-auto tw-translate-y-0'}`}>
			<div className="tw-flex tw-gap-10px tw-items-center">
				<select
					value={selectedStation}
					onChange={(e) => setSelectedStation(e.target.value)}
					className="tw-flex-1 tw-p-10px tw-rounded-3px tw-bg-white tw-text-secondary tw-font-secondary-normal tw-uppercase tw-tracking-wide tw-border-none tw-cursor-pointer"
				>
					<option value="all">All Stations</option>
					{availableStations.map(station => (
						<option key={station} value={station}>Station {station}</option>
					))}
				</select>
				<button
					onClick={() => {
						// Capture snapshot of current form state when toggling filter
						// Only capture if we're turning the filter ON (not off)
						if (!showIncompleteOnly) {
							setFilterSnapshot({ ...formValues });
						}
						setShowIncompleteOnly(!showIncompleteOnly);
					}}
					className={`tw-w-[170px] tw-p-10px tw-rounded-3px tw-font-secondary-normal tw-uppercase tw-tracking-wide tw-border-none tw-cursor-pointer tw-whitespace-nowrap tw-transition-colors tw-h-[41px] ${
						showIncompleteOnly
							? 'tw-bg-primary tw-text-secondary'
							: 'tw-bg-white tw-text-secondary'
					}`}
					title={showIncompleteOnly ? "Showing incomplete items only" : "Showing all items"}
				>
					<span className="tw-px-5px tw-pointer-events-none">{showIncompleteOnly ? 'Show All' : '⚠ Remaining'}</span>
				</button>
			</div>
			<div className="tw-mt-10px">
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					placeholder="Search by category, section, title, or field name..."
					className="tw-w-full tw-p-10px tw-rounded-3px tw-bg-white tw-text-secondary tw-font-secondary-normal tw-tracking-wide tw-border-none"
				/>
			</div>
		</div>
		<div className="[&_>div+div]:tw-mt-40px tw-relative tw-pb-60px">
			{/* Loading Overlay */}
			{isLoading && (
				<div className="tw-fixed tw-inset-0 tw-bg-black/80 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-backdrop-blur-sm">
					<div className="tw-bg-white tw-rounded-6px tw-p-40px tw-shadow-2xl tw-flex tw-flex-col tw-items-center tw-gap-20px">
						<div className="tw-w-60px tw-h-60px tw-border-4px tw-border-solid tw-border-white/20 tw-border-t-secondary tw-rounded-full tw-animate-spin"></div>
						<div className="tw-text-secondary tw-font-secondary-bold tw-text-xl tw-uppercase tw-tracking-widest">
							Loading...
						</div>
					</div>
				</div>
			)}

			{/* Saving Overlay */}
			{isSaving && (
				<div className="tw-fixed tw-inset-0 tw-bg-black/80 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-backdrop-blur-sm">
					<div className="tw-bg-white tw-rounded-6px tw-p-40px tw-shadow-2xl tw-flex tw-flex-col tw-items-center tw-gap-20px">
						<div className="tw-w-60px tw-h-60px tw-border-4px tw-border-solid tw-border-white/20 tw-border-t-secondary tw-rounded-full tw-animate-spin"></div>
						<div className="tw-text-secondary tw-font-secondary-bold tw-text-xl tw-uppercase tw-tracking-widest">
							Saving...
						</div>
					</div>
				</div>
			)}

			{/* Category Navigation */}
			<button
				onClick={() => setCategoryNavOpen(!categoryNavOpen)}
				className="tw-fixed tw-bottom-90px md:tw-bottom-20px tw-right-20px tw-w-50px tw-h-50px tw-rounded-full tw-bg-white tw-text-secondary tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-cursor-pointer tw-border-none tw-z-50"
				aria-label="Category navigation"
			>
				<i className="fas fa-list"></i>
			</button>

			{categoryNavOpen && (
				<div className="tw-fixed tw-inset-0 tw-bg-black/80 tw-z-50 tw-flex tw-items-center tw-justify-center" onClick={() => setCategoryNavOpen(false)}>
					<div className="tw-bg-secondary tw-rounded-6px tw-p-20px tw-w-[90vw] md:tw-w-auto md:tw-min-w-[500px] tw-h-[80vh] tw-overflow-y-auto" onClick={(e) => e.stopPropagation()}>
						<div className="tw-flex tw-justify-between tw-items-center tw-mb-20px">
							<h3 className="tw-text-xl tw-font-secondary-bold tw-text-white tw-uppercase tw-tracking-widest">Categories</h3>
							<button onClick={() => setCategoryNavOpen(false)} className="tw-text-white tw-bg-transparent tw-border-none tw-text-2xl tw-cursor-pointer">
								<i className="fas fa-times"></i>
							</button>
						</div>
						<div className="tw-mb-15px">
							<div className="tw-flex tw-gap-10px tw-items-center">
								<select
									value={selectedStation}
									onChange={(e) => setSelectedStation(e.target.value)}
									className="tw-flex-1 tw-p-10px tw-rounded-3px tw-bg-white tw-text-secondary tw-font-secondary-normal tw-uppercase tw-tracking-wide tw-border-none tw-cursor-pointer"
									onClick={(e) => e.stopPropagation()}
								>
									<option value="all">All Stations</option>
									{availableStations.map(station => (
										<option key={station} value={station}>Station {station}</option>
									))}
								</select>
								<button
									onClick={(e) => {
										e.stopPropagation();
										// Capture snapshot of current form state when toggling filter
										// Only capture if we're turning the filter ON (not off)
										if (!showIncompleteOnly) {
											setFilterSnapshot({ ...formValues });
										}
										setShowIncompleteOnly(!showIncompleteOnly);
									}}
									className={`tw-w-[170px] tw-p-10px tw-rounded-3px tw-font-secondary-normal tw-uppercase tw-tracking-wide tw-border-none tw-cursor-pointer tw-whitespace-nowrap tw-transition-colors tw-h-[41px] ${
										showIncompleteOnly
											? 'tw-bg-primary tw-text-secondary'
											: 'tw-bg-white tw-text-secondary'
									}`}
									title={showIncompleteOnly ? "Showing incomplete items only" : "Showing all items"}
								>
									{showIncompleteOnly ? '⚠ Incomplete' : 'All Items'}
								</button>
							</div>
						</div>
						<ul className="tw-space-y-10px">
							{filteredCategories?.map((category, ci) => (
								<li key={ci} className="tw-space-y-5px">
									<button
										onClick={() => scrollToCategory(ci)}
										className="tw-w-full tw-text-left tw-bg-white/10 hover:tw-bg-white/20 tw-text-white tw-px-15px tw-py-10px tw-rounded-3px tw-border-none tw-cursor-pointer tw-font-secondary-normal tw-tracking-wide tw-uppercase tw-transition-all"
									>
										{ci + 1}. {category.category}
									</button>
									{category.sections && category.sections.length > 0 && (
										<ul className="tw-ml-15px tw-space-y-5px">
											{category.sections.map((section, si) => (
												<li key={si}>
													<button
														onClick={() => scrollToSection(ci, si)}
														className="tw-w-full tw-text-left tw-bg-white/5 hover:tw-bg-white/10 tw-text-white/80 tw-px-10px tw-py-8px tw-rounded-3px tw-border-none tw-cursor-pointer tw-font-secondary-light tw-text-sm tw-tracking-wide tw-uppercase tw-transition-all"
													dangerouslySetInnerHTML={{ __html: `${ci + 1}.${si + 1} ${section.name}` }}
													/>
												</li>
											))}
										</ul>
									)}
								</li>
							))}
						</ul>
					</div>
				</div>
			)}

			{
				filteredCategories?.flatMap((category, ci) =>
					category.sections?.flatMap((section, si) => {
						// Items are already filtered by filteredCategories
						// Also filter out cqc-gen items if unit model is not "Flock Gen Trailer"
						const filteredItems = section.items?.filter(item => {
							if (item.cqc === 'cqc-gen' && formValues?.unit_identifiers_unit_model !== "Flock Gen Trailer") {
								return false;
							}
							return true;
						});

						// Skip rendering if no items
						if (!filteredItems || filteredItems.length === 0) return null;

						return <div key={`${ci}-${si}`} id={`category-${ci}-section-${si}`}>
							{si === 0 && <h2 id={`category-${ci}`} className={`${!formValues.unit_identifiers_unit_identifier_name && category.category !== 'Overview' ? '[&_*]:tw-select-none tw-select-none tw-grayscale tw-opacity-20 tw-blur-sm' : 'tw-sticky tw-top-0 tw-z-5'} tw-flex tw-flex-col tw-text-3xl tw-font-secondary-bold tw-tracking-widest tw-bg-black tw-rounded-3px tw-overflow-clip tw-mb-20d tw-text-white tw-px-10px tw-text-center tw-py-20px tw-leading-none tw-uppercase tw-mb-20d tw-z-4`}>
								{category.category}
							</h2>}
							<div className={`${!formValues.unit_identifiers_unit_identifier_name && category.category !== 'Overview' ? '[&_*]:tw-select-none tw-select-none tw-grayscale tw-opacity-20 tw-blur-sm' : ''} tw-border-1px tw-overflow-clip tw-rounded-3px tw-border-white/20 tw-shadow-lg tw-bg-black/20 tw-border-solid tw-border-white`}>
								{section.name && <h3 className={`${!formValues.unit_identifiers_unit_identifier_name && category.category !== 'Overview' ? '[&_*]:tw-select-none tw-select-none tw-grayscale tw-opacity-20 tw-blur-sm' : 'tw-sticky tw-top-0 tw-z-6'} tw-flex tw-flex-col tw-text-2xl tw-font-secondary-light tw-tracking-widest tw-bg-white tw-text-secondary tw-px-10px tw-text-center tw-py-10px tw-leading-none tw-uppercase tw-mb-20d tw-z-5`}>
									<span className="tw-w-full tw-text-[.5em] tw-mb-5p">
										{formValues.unit_identifiers_unit_identifier_name && <><b className="tw-uppercase tw-font-quaternary-bold tw-font-quaternary">{formValues.unit_identifiers_unit_identifier_name}</b>{` `}::</>}{` `}Category {si + 1}{` `}::{` `}{category.category}
									</span>
									<span className="tw-w-full">
										Section {ci + 1}:{` `}
										<span dangerouslySetInnerHTML={{ __html: section.name }} />
									</span>
								</h3>}
								<div className="tw-p-20d tw-space-y-40d [&_*]:tw-select-none">
									{filteredItems.map((item, ii) => {
										const isSignOffItem = item.form?.some(field => field.id === 'final_signoff_user');
										const blockSignOff = isSignOffItem && isVersionAboveV3 && hasIncompleteRequiredFields;

										return (
											<div key={`${ci}-${si}-${ii}`}>
												{blockSignOff && (
													<div className="tw-bg-red-900/40 tw-border-1px tw-border-solid tw-border-red-500/60 tw-rounded-3px tw-p-15px tw-mb-20d">
														<p className="tw-text-red-300 tw-font-secondary-normal tw-text-sm tw-tracking-wide tw-uppercase tw-m-0 tw-mb-10px">
															Required items must be completed before you can sign off
														</p>
														<ul className="tw-list-disc tw-pl-20px tw-m-0 tw-space-y-2px">
															{incompleteRequiredFields.map(field => (
																<li key={field.id} className="tw-text-red-300/80 tw-text-xs tw-font-secondary-light" dangerouslySetInnerHTML={{ __html: field.label }} />
															))}
														</ul>
													</div>
												)}
												<div className={blockSignOff ? 'tw-opacity-40 tw-pointer-events-none tw-select-none' : ''}>
													<QItem isEditMode={isEditMode} vinsArrays={vinsArrays} form={item.form} hideCheckbox={item.hideCheckbox} slug={item.slug} initialLoadValues={initialLoadValues} formValues={formValues} setFormValues={setFormValues} categoryNumber={ci + 1} sectionNumber={si + 1} number={ii + 1} name={item.name} category={category.category} section={section.name} description={item.description} cqc={item.cqc || null} images={item.images} setHasDuplicateVIN={setHasDuplicateVIN} />
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					}
					))
			}
			{itemsChanged && <>
				<div className="tw-fixed md:tw-left-1/2 md:-tw-translate-x-1/2 tw-pb-[30px] tw-bottom-[-1px] md:tw-bottom-20px tw-left-0px tw-right-0px md:tw-right-auto tw-z-50 tw-p-10px md:tw-p-0px tw-bg-black/50 md:tw-bg-transparent tw-backdrop-blur-sm md:tw-backdrop-blur-none tw-flex md:tw-min-w-300d-alt">
					<ButtonLight default className={`tw-flex-grow ${hasDuplicateVIN ? 'tw-opacity-50 tw-cursor-not-allowed' : 'tw-cursor-pointer'}`} onClick={hasDuplicateVIN ? null : handleSave} disabled={hasDuplicateVIN}>
						<span className="tw-w-full tw-text-center tw-font-bold tw-uppercase tw-py-10d tw-px-40d tw-flex tw-items-center tw-justify-center tw-select-none [&_*]:tw-select-none">
							<span className="tw-uppercase">
								{hasDuplicateVIN ? "Cannot Save - Duplicate VIN" : isEditMode ? "Save" + (formValues.unit_identifiers_unit_identifier_name ? ` '${formValues.unit_identifiers_unit_identifier_name.toUpperCase()}'` : '') : "SAVE CHANGES"}
							</span>
						</span>
					</ButtonLight>
					{/* <div className="tw-backdrop-blur-sm tw-p-10px tw-rounded-6px tw-border-1px tw-border-solid tw-border-white/20 tw-shadow-lg tw-z-50">
						<span className="tw-text-sm tw-font-secondary tw-tracking-wide tw-text-white/70">
							You have unsaved changes
						</span>
					</div> */}
				</div>
			</>}
		</div>
	</>
}
